import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  ChevronRight, ChevronDown, Save, Download, RefreshCw, Eye,
  Sparkles, Database, Check, X, ArrowUpDown,
  Undo2, Redo2, GripVertical, Filter, Edit3,
  Table2, PieChart, MoreVertical, FolderOpen, Headphones, Send,
  CheckCircle,
} from 'lucide-react'
import type { DataAsset } from '@/types'
import { cn } from '@/lib/utils'
// import { AIChatModal } from '@/components/catalog/AIChatModal'  // 已隐藏
import { PAYROLL_REPORT_FIELDS, PAYROLL_TABLE_DATA } from '@/data/payroll-report'
import { CORP_BRANCH_REPORT_FIELDS, CORP_BRANCH_TABLE_DATA, CORP_BRANCH_CORP_FIELDS, CORP_BRANCH_PERSONAL_FIELDS } from '@/data/corp-branch-deposit-report'
import { INCLUSIVE_LOAN_FIELDS, INCLUSIVE_LOAN_TABLE_DATA } from '@/data/inclusive-loan-report'
import { CORP_CUSTOMER_FIELDS, CORP_CUSTOMER_TABLE_DATA } from '@/data/corp-customer-report'
import { CORP_NEW_CUSTOMER_FIELDS, CORP_NEW_CUSTOMER_TABLE_DATA } from '@/data/corp-new-customer-report'
import { PERSONAL_CUSTOMER_DETAIL_FIELDS, PERSONAL_CUSTOMER_DETAIL_DATA } from '@/data/personal-customer-detail-report'
import { SHARED_TEMPLATES } from '@/data/shared-templates'
import { CATALOG_DETAILS } from '@/data/catalog-details'

/* ---- 字段定义 ---- */
export interface Field {
  id: string
  name: string
  type: 'dimension' | 'measure'
  group: string
  dataType?: string
}

export const REPORT_FIELDS: Field[] = [
  // 维度
  { id: 'd1', name: '统计日期', type: 'dimension', group: '时间信息' },
  { id: 'f1', name: '支行名称', type: 'dimension', group: '机构信息' },
  { id: 'f2', name: '分行名称', type: 'dimension', group: '机构信息' },
  { id: 'f3', name: '客户名称', type: 'dimension', group: '客户信息' },
  { id: 'f4', name: '跨行最大转入交易金额对手', type: 'dimension', group: '交易信息' },
  { id: 'f5', name: '跨行最大转出交易金额对手', type: 'dimension', group: '交易信息' },
  // 度量
  { id: 'f6', name: '存款余额', type: 'measure', group: '存款信息', dataType: '万元' },
  { id: 'f7', name: '比上日', type: 'measure', group: '存款信息', dataType: '万元' },
  { id: 'f8', name: '跨行转入金额', type: 'measure', group: '交易信息', dataType: '万元' },
  { id: 'f9', name: '跨行转出金额', type: 'measure', group: '交易信息', dataType: '万元' },
  { id: 'f10', name: '跨行最大转入交易金额', type: 'measure', group: '交易信息', dataType: '万元' },
  { id: 'f11', name: '跨行最大转出交易金额', type: 'measure', group: '交易信息', dataType: '万元' },
  { id: 'f12', name: '跨行最大转入交易金额对手行', type: 'dimension', group: '交易信息' },
  { id: 'f13', name: '跨行最大转出交易金额对手行', type: 'dimension', group: '交易信息' },
]

/* ---- 模拟表格数据 ---- */
const TABLE_DATA = [
  { d1: '2026-06-10', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州海珠科技有限公司', f4: '广州海珠科技有限公司', f5: '广州海珠科技有限公司', f6: '520.00', f7: '-750.00', f8: '150.00', f9: '850.00', f10: '120.00', f11: '680.00', f12: '中国工商银行广州天河支行', f13: '中国农业银行深圳福田支行' },
  { d1: '2026-06-10', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州昌岗投资管理有限公司', f4: '广州昌岗投资管理有限公司', f5: '广州昌岗投资管理有限公司', f6: '1,350.00', f7: '-2,200.00', f8: '280.00', f9: '720.00', f10: '200.00', f11: '550.00', f12: '中国建设银行广州市越秀支行', f13: '中国招商银行广州分行' },
  { d1: '2026-06-10', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州新港实业发展有限公司', f4: '广州新港实业发展有限公司', f5: '东莞南城贸易有限公司', f6: '890.00', f7: '-1,035.00', f8: '120.00', f9: '680.00', f10: '95.00', f11: '60.00', f12: '广东华兴银行股份有限公司广州分行', f13: '中国银行东莞分行' },
  { d1: '2026-06-10', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州赤岗金融服务有限公司', f4: '广州赤岗金融服务有限公司', f5: '佛山禅城区商贸公司', f6: '680.00', f7: '-1,050.00', f8: '100.00', f9: '900.00', f10: '80.00', f11: '95.00', f12: '中国工商银行广州海珠支行', f13: '广发银行佛山分行' },
  { d1: '2026-06-10', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州琶洲会展有限公司', f4: '惠州惠城区贸易公司', f5: '广州琶洲会展有限公司', f6: '2,100.00', f7: '+1,500.00', f8: '450.00', f9: '200.00', f10: '380.00', f11: '160.00', f12: '中国邮政储蓄银行广州分行', f13: '中国民生银行广州天河支行' },
  { d1: '2026-06-10', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州海珠投资咨询有限公司', f4: '广州海珠投资咨询有限公司', f5: '广州海珠投资咨询有限公司', f6: '750.00', f7: '-1,980.00', f8: '90.00', f9: '650.00', f10: '70.00', f11: '520.00', f12: '交通银行广东省分行', f13: '兴业银行广州分行' },
  { d1: '2026-06-10', f1: '工业大道支行', f2: '海珠中心支行', f3: '广州工业大道物流有限公司', f4: '深圳南山贸易有限公司', f5: '深圳南山贸易有限公司', f6: '430.00', f7: '-2,015.00', f8: '80.00', f9: '780.00', f10: '65.00', f11: '75.00', f12: '中国光大银行广州分行', f13: '平安银行深圳分行' },
  { d1: '2026-06-10', f1: '工业大道支行', f2: '海珠中心支行', f3: '广州石溪建材市场有限公司', f4: '广州石溪建材市场有限公司', f5: '佛山顺德商贸有限公司', f6: '1,680.00', f7: '-1,150.00', f8: '320.00', f9: '180.00', f10: '280.00', f11: '620.00', f12: '中信银行广州分行', f13: '广东华兴银行股份有限公司佛山分行' },
  { d1: '2026-06-10', f1: '广州广报中心支行', f2: '海珠中心支行', f3: '广州广报文化传媒有限公司', f4: '广州广报文化传媒有限公司', f5: '广州荔湾贸易有限公司', f6: '360.00', f7: '-1,988.00', f8: '60.00', f9: '45.00', f10: '50.00', f11: '35.00', f12: '华夏银行广州分行', f13: '浦发银行广州分行' },
  { d1: '2026-06-10', f1: '广州广报中心支行', f2: '海珠中心支行', f3: '广州珠江新城投资有限公司', f4: '东莞虎门商贸有限公司', f5: '广州珠江新城投资有限公司', f6: '2,850.00', f7: '+2,200.00', f8: '600.00', f9: '250.00', f10: '500.00', f11: '200.00', f12: '中国工商银行广州市珠江新城支行', f13: '中国建设银行广州番禺支行' },
  // 2026-06-09 相同分支行数据
  { d1: '2026-06-09', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州海珠科技有限公司', f4: '广州海珠科技有限公司', f5: '广州海珠科技有限公司', f6: '1,770.00', f7: '+320.00', f8: '200.00', f9: '150.00', f10: '180.00', f11: '120.00', f12: '中国工商银行广州天河支行', f13: '中国农业银行深圳福田支行' },
  { d1: '2026-06-09', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州昌岗投资管理有限公司', f4: '广州昌岗投资管理有限公司', f5: '广州昌岗投资管理有限公司', f6: '3,550.00', f7: '+800.00', f8: '350.00', f9: '200.00', f10: '300.00', f11: '180.00', f12: '中国建设银行广州市越秀支行', f13: '中国招商银行广州分行' },
  { d1: '2026-06-09', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州新港实业发展有限公司', f4: '广州新港实业发展有限公司', f5: '广州新港实业发展有限公司', f6: '1,925.00', f7: '+560.00', f8: '180.00', f9: '320.00', f10: '150.00', f11: '95.00', f12: '广东华兴银行股份有限公司广州分行', f13: '中国银行东莞分行' },
  { d1: '2026-06-09', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州赤岗金融服务有限公司', f4: '广州赤岗金融服务有限公司', f5: '广州赤岗金融服务有限公司', f6: '1,730.00', f7: '+420.00', f8: '150.00', f9: '280.00', f10: '120.00', f11: '160.00', f12: '中国工商银行广州海珠支行', f13: '广发银行佛山分行' },
  { d1: '2026-06-09', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州琶洲会展有限公司', f4: '广州琶洲会展有限公司', f5: '广州琶洲会展有限公司', f6: '600.00', f7: '-350.00', f8: '120.00', f9: '480.00', f10: '95.00', f11: '400.00', f12: '中国邮政储蓄银行广州分行', f13: '中国民生银行广州天河支行' },
  { d1: '2026-06-09', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州海珠投资咨询有限公司', f4: '广州海珠投资咨询有限公司', f5: '广州海珠投资咨询有限公司', f6: '2,730.00', f7: '+1,100.00', f8: '280.00', f9: '350.00', f10: '250.00', f11: '180.00', f12: '交通银行广东省分行', f13: '兴业银行广州分行' },
  { d1: '2026-06-09', f1: '工业大道支行', f2: '海珠中心支行', f3: '广州工业大道物流有限公司', f4: '广州工业大道物流有限公司', f5: '广州工业大道物流有限公司', f6: '2,445.00', f7: '+680.00', f8: '220.00', f9: '380.00', f10: '200.00', f11: '150.00', f12: '中国光大银行广州分行', f13: '平安银行深圳分行' },
  { d1: '2026-06-09', f1: '工业大道支行', f2: '海珠中心支行', f3: '广州石溪建材市场有限公司', f4: '广州石溪建材市场有限公司', f5: '广州石溪建材市场有限公司', f6: '2,830.00', f7: '+950.00', f8: '480.00', f9: '320.00', f10: '350.00', f11: '280.00', f12: '中信银行广州分行', f13: '广东华兴银行股份有限公司佛山分行' },
  { d1: '2026-06-09', f1: '广州广报中心支行', f2: '海珠中心支行', f3: '广州广报文化传媒有限公司', f4: '广州广报文化传媒有限公司', f5: '广州广报文化传媒有限公司', f6: '2,348.00', f7: '+720.00', f8: '180.00', f9: '250.00', f10: '150.00', f11: '120.00', f12: '华夏银行广州分行', f13: '浦发银行广州分行' },
  { d1: '2026-06-09', f1: '广州广报中心支行', f2: '海珠中心支行', f3: '广州珠江新城投资有限公司', f4: '广州珠江新城投资有限公司', f5: '广州珠江新城投资有限公司', f6: '650.00', f7: '-280.00', f8: '90.00', f9: '380.00', f10: '70.00', f11: '320.00', f12: '中国工商银行广州市珠江新城支行', f13: '中国建设银行广州番禺支行' },
  // 6月9日 比上日减少超1000万数据
  { d1: '2026-06-09', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州天河房地产开发有限公司', f4: '广州天河房地产开发有限公司', f5: '深圳福田贸易有限公司', f6: '3,280.00', f7: '-3,500.00', f8: '420.00', f9: '1,200.00', f10: '350.00', f11: '980.00', f12: '中国工商银行广州天河支行', f13: '平安银行深圳福田支行' },
  { d1: '2026-06-09', f1: '赤岗支行', f2: '海珠中心支行', f3: '广州白云仓储物流有限公司', f4: '广州白云仓储物流有限公司', f5: '广州白云仓储物流有限公司', f6: '890.00', f7: '-2,800.00', f8: '180.00', f9: '950.00', f10: '160.00', f11: '720.00', f12: '中国建设银行广州白云支行', f13: '招商银行广州分行' },
  { d1: '2026-06-09', f1: '工业大道支行', f2: '海珠中心支行', f3: '广州越秀供应链管理有限公司', f4: '东莞长安商贸有限公司', f5: '广州越秀供应链管理有限公司', f6: '1,560.00', f7: '-1,850.00', f8: '250.00', f9: '680.00', f10: '220.00', f11: '580.00', f12: '中国农业银行广州越秀支行', f13: '中信银行广州分行' },
  { d1: '2026-06-09', f1: '广州广报中心支行', f2: '海珠中心支行', f3: '广州番禺精密制造有限公司', f4: '广州番禺精密制造有限公司', f5: '广州番禺精密制造有限公司', f6: '2,150.00', f7: '-4,200.00', f8: '580.00', f9: '1,500.00', f10: '480.00', f11: '1,350.00', f12: '中国银行广州番禺支行', f13: '广发银行广州分行' },
  { d1: '2026-06-09', f1: '昌岗路支行', f2: '海珠中心支行', f3: '广州黄埔信息科技有限公司', f4: '广州黄埔信息科技有限公司', f5: '佛山南海贸易有限公司', f6: '620.00', f7: '-1,680.00', f8: '120.00', f9: '520.00', f10: '95.00', f11: '450.00', f12: '中国民生银行广州黄埔支行', f13: '广东华兴银行股份有限公司广州分行' },
]

const FIELD_COLUMNS = [
  { key: 'd1', label: '统计日期', width: '100px', align: 'left' as const },
  { key: 'f1', label: '支行名称', width: '120px', align: 'left' as const },
  { key: 'f2', label: '分行名称', width: '100px', align: 'left' as const },
  { key: 'f3', label: '客户名称', width: '100px', align: 'left' as const },
  { key: 'f6', label: '存款余额(万元)', width: '110px', align: 'right' as const },
  { key: 'f7', label: '比上日(万元)', width: '110px', align: 'right' as const },
  { key: 'f8', label: '跨行转入(万元)', width: '110px', align: 'right' as const },
  { key: 'f9', label: '跨行转出(万元)', width: '110px', align: 'right' as const },
  { key: 'f10', label: '跨行最大转入(万元)', width: '120px', align: 'right' as const },
  { key: 'f4', label: '跨行最大转入对手', width: '160px', align: 'left' as const },
  { key: 'f12', label: '跨行最大转入对手行', width: '180px', align: 'left' as const },
  { key: 'f11', label: '跨行最大转出(万元)', width: '120px', align: 'right' as const },
  { key: 'f5', label: '跨行最大转出对手', width: '160px', align: 'left' as const },
  { key: 'f13', label: '跨行最大转出对手行', width: '180px', align: 'left' as const },
]

/* ---- 过滤条件 ---- */
export interface FilterCondition {
  id: string
  field: Field
  operator: string
  value: string
}

const DIMENSION_OPERATORS = [
  { value: 'contains', label: '包含' },
  { value: 'eq', label: '等于' },
  { value: 'neq', label: '不等于' },
]

const MEASURE_OPERATORS = [
  { value: 'gte', label: '大于等于' },
  { value: 'lte', label: '小于等于' },
  { value: 'gt', label: '大于' },
  { value: 'lt', label: '小于' },
  { value: 'eq', label: '等于' },
]

/* ---- 透视分析聚合类型 ---- */
export type AggregationType = 'sum' | 'avg' | 'max' | 'min' | 'vsPrevDay' | 'vsPrevMonth' | 'vsPrevQuarter' | 'vsYearStart'

export const AGGREGATION_OPTIONS: { value: AggregationType; label: string }[] = [
  { value: 'sum', label: '汇总值' },
  { value: 'avg', label: '平均值' },
  { value: 'max', label: '最大值' },
  { value: 'min', label: '最小值' },
  { value: 'vsPrevDay', label: '比上日' },
  { value: 'vsPrevMonth', label: '比上月' },
  { value: 'vsPrevQuarter', label: '比上季' },
  { value: 'vsYearStart', label: '比年初' },
]

/** 计算字段 */
export interface ComputedMeasure {
  id: string
  sourceFieldId: string
  name: string
  aggType: AggregationType
}

/** 计算字段表达式节点 */
type ExpressionNode =
  | { type: 'field'; fieldId: string; aggType: AggregationType }
  | { type: 'operator'; value: '+' | '-' | '*' | '/' }
  | { type: 'number'; value: string }
  | { type: 'paren'; value: '(' | ')' }

/** 自定义计算字段 */
export interface CalculatedField {
  id: string
  name: string
  expression: ExpressionNode[]
}

/** 解析数值（去掉逗号、+号） */
function parseNum(val: string): number {
  return parseFloat(val.replace(/[+,]/g, ''))
}

/** 格式化数值 */
function formatNum(val: number): string {
  const abs = Math.abs(val)
  const formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return val >= 0 ? formatted : `-${formatted}`
}

/** 获取时间对比类型的参考日期 */
function getRefDate(date: string, type: AggregationType): string | null {
  const [y, m, d] = date.split('-').map(Number)
  switch (type) {
    case 'vsPrevDay': {
      const prev = new Date(y, m - 1, d - 1)
      return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`
    }
    case 'vsPrevMonth': {
      const prevMonth = m === 1 ? 12 : m - 1
      const prevYear = m === 1 ? y - 1 : y
      return `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
    case 'vsPrevQuarter': {
      const quarter = Math.ceil(m / 3)
      const prevQuarter = quarter === 1 ? 4 : quarter - 1
      const prevYear = quarter === 1 ? y - 1 : y
      const prevMonthNum = prevQuarter === 1 ? 1 : prevQuarter === 2 ? 4 : prevQuarter === 3 ? 7 : 10
      return `${prevYear}-${String(prevMonthNum).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
    case 'vsYearStart': {
      // 比年初：与上年末（12月31日）的值对比
      return `${y - 1}-12-31`
    }
    default:
      return null
  }
}

/** 计算表达式值（支持括号） */
function evaluateExpression(
  expression: ExpressionNode[],
  measureValues: Record<string, number>,
): number | null {
  if (expression.length === 0) return null

  // 转为中缀记号列表（值 + 运算符 + 括号）
  const tokens: (number | string)[] = []
  for (const node of expression) {
    if (node.type === 'field') {
      const key = `${node.fieldId}_${node.aggType}`
      if (!(key in measureValues)) return null
      tokens.push(measureValues[key])
    } else if (node.type === 'number') {
      tokens.push(parseFloat(node.value))
    } else if (node.type === 'operator') {
      tokens.push(node.value)
    } else if (node.type === 'paren') {
      tokens.push(node.value)
    }
  }

  if (tokens.length === 0) return null

  // 使用调度场算法转逆波兰表达式（RPN）
  const output: number[] = []
  const opStack: string[] = []
  const precedence: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 }

  for (const token of tokens) {
    if (typeof token === 'number') {
      output.push(token)
    } else if (token === '(') {
      opStack.push(token)
    } else if (token === ')') {
      while (opStack.length > 0 && opStack[opStack.length - 1] !== '(') {
        output.push(NaN) // placeholder, handled below
      }
      // 弹出运算符而非数字
      while (opStack.length > 0 && opStack[opStack.length - 1] !== '(') {
        const op = opStack.pop()!
        output.push(NaN) // we need a different approach
      }
      opStack.pop() // 移除 '('
    } else {
      // 运算符
      while (
        opStack.length > 0 &&
        opStack[opStack.length - 1] !== '(' &&
        (precedence[opStack[opStack.length - 1]] || 0) >= precedence[token]
      ) {
        output.push(NaN) // placeholder
        opStack.pop()
      }
      opStack.push(token)
    }
  }

  // 重新实现：正确的调度场算法
  const rpn: (number | string)[] = []
  const stack: string[] = []

  for (const token of tokens) {
    if (typeof token === 'number') {
      rpn.push(token)
    } else if (token === '(') {
      stack.push(token)
    } else if (token === ')') {
      while (stack.length > 0 && stack[stack.length - 1] !== '(') {
        rpn.push(stack.pop()!)
      }
      stack.pop() // 移除 '('
    } else {
      // 运算符
      while (
        stack.length > 0 &&
        stack[stack.length - 1] !== '(' &&
        (precedence[stack[stack.length - 1]] || 0) >= precedence[token]
      ) {
        rpn.push(stack.pop()!)
      }
      stack.push(token)
    }
  }
  while (stack.length > 0) {
    rpn.push(stack.pop()!)
  }

  // 计算 RPN
  const evalStack: number[] = []
  for (const token of rpn) {
    if (typeof token === 'number') {
      evalStack.push(token)
    } else {
      if (evalStack.length < 2) return null
      const b = evalStack.pop()!
      const a = evalStack.pop()!
      switch (token) {
        case '+': evalStack.push(a + b); break
        case '-': evalStack.push(a - b); break
        case '*': evalStack.push(a * b); break
        case '/': if (b === 0) return null; evalStack.push(a / b); break
        default: return null
      }
    }
  }

  return evalStack.length === 1 ? evalStack[0] : null
}

/** 透视分析数据计算 */
interface PivotRow {
  dims: Record<string, string>
  measures: Record<string, { sum: number; count: number; max: number; min: number }>
  /** 按统计日期存储的度量汇总值（用于时间对比聚合） */
  dateSums?: Record<string, Record<string, number>>  // date -> measureId -> sum
}

function computePivotData(
  data: Record<string, string>[],
  allSelectedFields: string[],
  fields: Field[],
  computedFields: ComputedMeasure[],
  calculatedFields: CalculatedField[] = [],
  allData?: Record<string, string>[],  // 全量数据（用于时间对比查找，不受筛选影响）
) {
  const timeData = allData || data  // 时间对比用全量数据
  // 检查是否有时间对比聚合类型
  const hasTimeComparison = computedFields.some(cm =>
    ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(cm.aggType)
  )

  // 动态获取日期字段ID（支持 d1 和 data_date）
  const dateFieldId = fields.find(f => f.type === 'dimension' && f.dataType === 'date')?.id || 'd1'

  // 收集所有日期并排序（从全量数据中取）
  const allDates = new Set<string>()
  for (const row of timeData) {
    if (row && row[dateFieldId as keyof typeof row]) allDates.add(row[dateFieldId as keyof typeof row] as string)
  }
  const sortedDates = [...allDates].sort()

  // 辅助：获取参考日期索引
  const getRefDateIdx = (date: string, type: AggregationType): number => {
    const idx = sortedDates.indexOf(date)
    if (idx < 0) return -1
    switch (type) {
      case 'vsPrevDay': {
        // 比上日：查找日历上恰好前一天的数据
        const [y, m, d] = date.split('-').map(Number)
        const prev = new Date(y, m - 1, d - 1)
        const formatted = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`
        return sortedDates.indexOf(formatted)
      }
      case 'vsPrevMonth': {
        // 比上月：查找上个月的数据
        const [y, m] = date.split('-').map(Number)
        const prevMonth = m === 1 ? 12 : m - 1
        const prevYear = m === 1 ? y - 1 : y
        const target = `${prevYear}-${String(prevMonth).padStart(2, '0')}`
        // 找到属于目标年月的日期索引（取最接近当前日期的那个）
        let bestIdx = -1
        for (let i = idx - 1; i >= 0; i--) {
          if (sortedDates[i].startsWith(target)) {
            bestIdx = i
            break
          }
        }
        return bestIdx
      }
      case 'vsPrevQuarter': {
        const [y, m] = date.split('-').map(Number)
        const q = Math.ceil(m / 3)
        for (let i = idx - 1; i >= 0; i--) {
          const [ry, rm] = sortedDates[i].split('-').map(Number)
          const rq = Math.ceil(rm / 3)
          if (ry < y || rq < q) return i
        }
        return -1
      }
      case 'vsYearStart': {
        // 比年初：与上年末（12月31日）的值对比
        const [y] = date.split('-').map(Number)
        const prevYearEnd = `${y - 1}-12-31`
        return sortedDates.indexOf(prevYearEnd)
      }
      default: return -1
    }
  }

  const hasDateDim = allSelectedFields.includes(dateFieldId)
  const groups = new Map<string, PivotRow>()

  // 预计算全局时间对比查找表：按非日期维度分组 → 日期 → cmId→sum值
  const globalDateMap = hasTimeComparison
    ? new Map<string, Map<string, Record<string, number>>>()  // nonDateKey → date → cmId→sum
    : null
  if (globalDateMap) {
    const nonDateDims = allSelectedFields.filter(f =>
      fields.find(rf => rf.id === f)?.type === 'dimension' && f !== dateFieldId
    )
    for (const row of timeData) {
      if (!row) continue
      const nonDateKey = nonDateDims.map(f => row[f] as string).join('|||')
      const date = row[dateFieldId as keyof typeof row] as string
      if (!date) continue
      if (!globalDateMap.has(nonDateKey)) {
        globalDateMap.set(nonDateKey, new Map())
      }
      const dateMap = globalDateMap.get(nonDateKey)!
      if (!dateMap.has(date)) {
        const entry: Record<string, number> = {}
        for (const cm of computedFields) {
          entry[cm.id] = parseNum(row[cm.sourceFieldId] as string)
        }
        dateMap.set(date, entry)
      } else {
        const entry = dateMap.get(date)!
        for (const cm of computedFields) {
          entry[cm.id] += parseNum(row[cm.sourceFieldId] as string)
        }
      }
    }
  }

  // 当有时间对比且d1是维度时，构建跨组查找：非日期维度 -> 日期 -> cmId->value
  const dateCrossRef = hasTimeComparison && hasDateDim ? globalDateMap : null

  // 收集计算字段引用的所有度量字段ID
  const calcFieldRefs = new Set<string>()
  for (const cf of calculatedFields) {
    for (const node of cf.expression) {
      if (node.type === 'field') calcFieldRefs.add(node.fieldId)
    }
  }

  for (const row of data) {
    const dimKey = allSelectedFields
      .filter(f => fields.find(rf => rf.id === f)?.type === 'dimension')
      .map(f => row[f] as string).join('|||')

    // 非日期维度key（用于跨组查找）
    const nonDateKey = allSelectedFields
      .filter(f => fields.find(rf => rf.id === f)?.type === 'dimension' && f !== dateFieldId)
      .map(f => row[f] as string).join('|||')
    if (!groups.has(dimKey)) {
      const dimRecord: Record<string, string> = {}
      for (const f of allSelectedFields) {
        if (fields.find(rf => rf.id === f)?.type === 'dimension') {
          dimRecord[f] = row[f] as string
        }
      }
      const measureRecord: Record<string, { sum: number; count: number; max: number; min: number }> = {}
      for (const cm of computedFields) {
        const num = parseNum(row[cm.sourceFieldId] as string)
        measureRecord[cm.id] = { sum: num, count: 1, max: num, min: num }
      }
      // 为计算字段引用的度量字段也初始化聚合记录
      for (const fid of calcFieldRefs) {
        if (!measureRecord[fid]) {
          const num = parseNum(row[fid] as string || '0')
          measureRecord[fid] = { sum: num, count: 1, max: num, min: num }
        }
      }
      // 初始化dateSums（用于无日期维度时的时间对比）
      let dateSums: Record<string, Record<string, number>> | undefined
      if (hasTimeComparison && !hasDateDim && sortedDates.length > 1) {
        dateSums = {}
        const date = row[dateFieldId as keyof typeof row] as string
        const dateEntry: Record<string, number> = {}
        for (const cm of computedFields) {
          dateEntry[cm.id] = parseNum(row[cm.sourceFieldId] as string)
        }
        for (const fid of calcFieldRefs) {
          dateEntry[fid] = parseNum(row[fid] as string || '0')
        }
        dateSums[date] = dateEntry
      }
      groups.set(dimKey, { dims: dimRecord, measures: measureRecord, dateSums })
    } else {
      const group = groups.get(dimKey)!
      for (const cm of computedFields) {
        const num = parseNum(row[cm.sourceFieldId] as string)
        const agg = group.measures[cm.id]
        agg.sum += num
        agg.count += 1
        if (num > agg.max) agg.max = num
        if (num < agg.min) agg.min = num
      }
      // 聚合计算字段引用的度量字段
      for (const fid of calcFieldRefs) {
        const agg = group.measures[fid]
        if (agg) {
          const num = parseNum(row[fid] as string || '0')
          agg.sum += num
          agg.count += 1
          if (num > agg.max) agg.max = num
          if (num < agg.min) agg.min = num
        }
      }
      // 更新日期Sums
      if (hasTimeComparison && !hasDateDim && sortedDates.length > 1 && group.dateSums) {
        const date = row[dateFieldId as keyof typeof row] as string
        if (!group.dateSums[date]) {
          group.dateSums[date] = {}
          for (const cm of computedFields) {
            group.dateSums[date][cm.id] = 0
          }
          for (const fid of calcFieldRefs) {
            group.dateSums[date][fid] = 0
          }
        }
        for (const cm of computedFields) {
          group.dateSums[date][cm.id] += parseNum(row[cm.sourceFieldId] as string)
        }
        for (const fid of calcFieldRefs) {
          group.dateSums[date][fid] += parseNum(row[fid] as string || '0')
        }
      }
    }
  }

  const result: Record<string, string>[] = []
  for (const [, group] of groups) {
    const record: Record<string, string> = { ...group.dims }
    for (const cm of computedFields) {
      const agg = group.measures[cm.id]
      const isTimeComparison = ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(cm.aggType)
      if (isTimeComparison) {
        if (hasDateDim && dateCrossRef) {
          // 日期维度：从跨组查找表中获取参考日期值
          const currentDate = group.dims[dateFieldId]
          if (currentDate) {
            const refIdx = getRefDateIdx(currentDate, cm.aggType)
            if (refIdx >= 0) {
              const refDate = sortedDates[refIdx]
              // nonDateKey 必须与数据收集时一致：按 allSelectedFields 顺序
              const nonDateKey = allSelectedFields
                .filter(f => fields.find(rf => rf.id === f)?.type === 'dimension' && f !== dateFieldId)
                .map(f => group.dims[f] || '').join('|||')
              const dateMap = dateCrossRef.get(nonDateKey)
              const refEntry = dateMap?.get(refDate)
              const currentEntry = dateMap?.get(currentDate)
              const currentVal = currentEntry?.[cm.id] ?? agg.sum
              const refVal = refEntry?.[cm.id]
              if (refVal !== undefined) {
                record[cm.id] = formatNum(currentVal - refVal)
              } else {
                record[cm.id] = '-'
              }
            } else {
              record[cm.id] = '-'
            }
          } else {
            record[cm.id] = '-'
          }
        } else if (!hasDateDim && group.dateSums) {
          // d1不是维度：按日期拆分输出
          for (const date of sortedDates) {
            const dateSumsEntry = group.dateSums[date]
            if (!dateSumsEntry) continue
            const refIdx = getRefDateIdx(date, cm.aggType)
            if (refIdx >= 0) {
              const refDate = sortedDates[refIdx]
              const refVal = group.dateSums[refDate]?.[cm.id]
              if (refVal !== undefined) {
                const splitRecord: Record<string, string> = { ...group.dims, d1: date }
                splitRecord[cm.id] = formatNum(dateSumsEntry[cm.id] - refVal)
                for (const cm2 of computedFields) {
                  if (cm2.id !== cm.id && !['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(cm2.aggType)) {
                    splitRecord[cm2.id] = formatNum(dateSumsEntry[cm2.id])
                  }
                }
                result.push(splitRecord)
              }
            }
          }
          continue
        } else {
          record[cm.id] = '-'
        }
      } else {
        switch (cm.aggType) {
          case 'sum': record[cm.id] = formatNum(agg.sum); break
          case 'avg': record[cm.id] = formatNum(agg.sum / agg.count); break
          case 'max': record[cm.id] = formatNum(agg.max); break
          case 'min': record[cm.id] = formatNum(agg.min); break
        }
      }
    }
    // 计算自定义字段
    for (const cf of calculatedFields) {
      // 先构建 measureValues 映射
      const measureValues: Record<string, number> = {}
      for (const node of cf.expression) {
        if (node.type === 'field') {
          const key = `${node.fieldId}_${node.aggType}`
          if (!(key in measureValues) && group.measures[node.fieldId]) {
            const agg = group.measures[node.fieldId]
            measureValues[`${node.fieldId}_sum`] = agg.sum
            measureValues[`${node.fieldId}_avg`] = agg.sum / agg.count
            measureValues[`${node.fieldId}_max`] = agg.max
            measureValues[`${node.fieldId}_min`] = agg.min
          }
        }
      }
      const val = evaluateExpression(cf.expression, measureValues)
      record[cf.id] = val !== null ? formatNum(val) : '-'
    }
    result.push(record)
  }

  return result
}

interface FilterBarProps {
  conditions: FilterCondition[]
  onAdd: (field: Field) => void
  onRemove: (id: string) => void
  onChange: (id: string, patch: Partial<FilterCondition>) => void
  onCommitChange: (id: string) => void
  onQuery: () => void
  onReset: () => void
  isDragOver: boolean
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (e: React.DragEvent) => void
}

function FilterBar({ conditions, onAdd: _onAdd, onRemove, onChange, onCommitChange, onQuery, onReset, isDragOver, onDragOver, onDragLeave, onDrop }: FilterBarProps) {
  return (
    <div className="bg-white border-b">
      {/* 条件筛选区标题 */}
      <div className="px-4 py-1.5 bg-gray-50 border-b flex items-center gap-1.5">
        <Filter className="w-3.5 h-3.5 text-gcb-primary" />
        <span className="text-xs font-medium text-gray-700">条件筛选区</span>
        <span className="text-[10px] text-gray-400 ml-1">将右侧字段拖拽到此处</span>
      </div>

      {/* 筛选条件内容 */}
      <div
        className={cn(
          "px-4 py-2 min-h-[48px] flex items-start gap-2 flex-wrap transition-colors",
          isDragOver && "bg-blue-50 border-2 border-dashed border-gcb-primary border-b-blue-200"
        )}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {conditions.length === 0 && !isDragOver && (
          <span className="text-xs text-gray-400 flex items-center gap-1 py-1">
            拖拽字段到此处添加筛选条件
          </span>
        )}
        {conditions.map((c) => {
          const isCalcFilter = c.field.id.startsWith('calc-')
          const isCmFilter = c.field.id.startsWith('cm-')
          const isComputedFilter = isCalcFilter || isCmFilter
          return (
          <div key={c.id} className="flex items-center gap-1.5 bg-gray-50 rounded-md p-1.5 border border-gray-200 hover:border-gcb-primary/40 transition-colors">
            <GripVertical className="w-3 h-3 text-gray-400 shrink-0 cursor-grab" />
            <span className={cn(
              "text-xs font-medium whitespace-nowrap px-1.5 py-0.5 rounded",
              isCalcFilter ? "bg-amber-50 text-amber-700" : isCmFilter ? "bg-purple-50 text-purple-700" : "bg-blue-50 text-gcb-primary"
            )}>{c.field.name}</span>
            <select
              value={c.operator}
              onChange={(e) => onChange(c.id, { operator: e.target.value })}
              onBlur={() => onCommitChange(c.id)}
              className="h-6 text-xs rounded border border-gray-200 bg-white px-1.5 focus:border-gcb-primary focus:outline-none"
            >
              {(c.field.type === 'dimension' ? DIMENSION_OPERATORS : MEASURE_OPERATORS).map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
            {(c.field.id === 'd1' || c.field.id === 'data_date') ? (
              <input
                type="date"
                value={c.value}
                onChange={(e) => onChange(c.id, { value: e.target.value })}
                onBlur={() => onCommitChange(c.id)}
                className="h-6 text-xs rounded border border-gray-200 bg-white px-1.5 focus:border-gcb-primary focus:outline-none"
              />
            ) : (
            <Input
              placeholder="请输入内容"
              value={c.value}
              onChange={(e) => onChange(c.id, { value: e.target.value })}
              onBlur={() => onCommitChange(c.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onCommitChange(c.id)
                  onQuery()
                }
              }}
              className="h-6 text-xs w-32 px-2"
            />
            )}
            <button
              onClick={() => onRemove(c.id)}
              className="text-gray-400 hover:text-red-500 transition-colors p-0.5"
              title="删除条件"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          )
        })}
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs px-3"
            onClick={onReset}
          >
            重置
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs px-4 bg-gcb-primary hover:bg-gcb-primaryDark"
            onClick={onQuery}
          >
            查询
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ---- 数据面板（右侧） ---- */
interface DataPanelProps {
  selectedFields: string[]
  onToggleField: (fieldId: string) => void
  onDragField: (field: Field) => void
  /** 是否透视模式 */
  isPivotMode: boolean
  /** 计算字段列表 */
  computedMeasures: ComputedMeasure[]
  onAddComputedMeasure: (sourceFieldId: string, aggType: AggregationType) => void
  onRemoveComputedMeasure: (id: string) => void
  /** 自定义计算字段 */
  calculatedFields: CalculatedField[]
  onAddCalculatedField: (field: CalculatedField) => void
  onUpdateCalculatedField: (field: CalculatedField) => void
  onRemoveCalculatedField: (id: string) => void
  /** 当前使用的字段定义 */
  fields: Field[]
  /** 字段元数据（业务定义与统计口径） */
  fieldMetadata?: Record<string, { definition: string; caliber: string }>
  /** 鼠标悬停字段变化回调 */
  onHoverField?: (field: { id: string; name: string; rect: DOMRect | null } | null) => void
}

function DataPanel({ selectedFields, onToggleField, onDragField: _onDragField, isPivotMode, computedMeasures, onAddComputedMeasure, onRemoveComputedMeasure, calculatedFields, onAddCalculatedField, onUpdateCalculatedField, onRemoveCalculatedField, fields, fieldMetadata, onHoverField }: DataPanelProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['机构信息', '客户信息', '交易信息', '存款信息']))
  const [openAggMenu, setOpenAggMenu] = useState<string | null>(null)

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      return next
    })
  }

  const groups = Array.from(new Set(fields.map(f => f.group)))

  const handleDragStart = (e: React.DragEvent, field: Field) => {
    e.dataTransfer.setData('text/plain', field.id)
    e.dataTransfer.effectAllowed = 'copy'
  }

  return (
    <div className="w-80 border-l bg-white flex flex-col">
      <div className="h-10 px-3 border-b flex items-center gap-2 bg-gradient-to-r from-gray-50 to-white">
        <Database className="w-4 h-4 text-gcb-primary" />
        <h3 className="font-semibold text-gray-900 text-xs">数据</h3>
      </div>
      <ScrollArea className="flex-1">
        <div className="py-2">
          {groups.map(group => {
            const isExpanded = expandedGroups.has(group)
            return (
              <div key={group}>
                <button
                  className="w-full flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                  onClick={() => toggleGroup(group)}
                >
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  }
                  <span className="font-medium">{group}</span>
                </button>
                {isExpanded && (
                  <div className="ml-4 space-y-0.5 pb-1">
                    {fields.filter(f => f.group === group).map(field => {
                      const isSelected = selectedFields.includes(field.id)
                      const isMeasureField = field.type === 'measure'
                      // 该字段的计算字段列表
                      const fieldComputed = computedMeasures.filter(c => c.sourceFieldId === field.id)
                      return (
                        <div key={field.id} className="relative">
                          <div
                            className={cn(
                              "flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors text-xs group/field",
                              isSelected
                                ? "bg-blue-50 text-gcb-primaryDark"
                                : "text-gray-600 hover:bg-gray-50"
                            )}
                            onClick={(e) => {
                              e.preventDefault()
                              onToggleField(field.id)
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, field)}
                            onMouseEnter={(e) => onHoverField?.({ id: field.id, name: field.name, rect: e.currentTarget.getBoundingClientRect() })}
                            onMouseLeave={() => onHoverField?.(null)}
                          >
                            <div
                              className={cn(
                                "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                                isSelected
                                  ? "bg-gcb-primary border-gcb-primary"
                                  : "border-gray-300 group-hover/field:border-gcb-primary"
                              )}
                            >
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <GripVertical className="w-3 h-3 text-gray-300 shrink-0 opacity-0 group-hover/field:opacity-100 transition-opacity cursor-grab" />
                            <span className="truncate flex-1 min-w-0">{field.name}</span>
                            {field.dataType && (
                              <span className="text-[10px] text-gray-400 shrink-0">{field.dataType}</span>
                            )}
                            {/* 透视模式下度量字段显示3个点菜单 */}
                            {isPivotMode && isMeasureField && (
                              <div className="relative shrink-0">
                                <button
                                  className="p-0.5 rounded hover:bg-gray-200 transition-colors"
                                  onMouseDown={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    setOpenAggMenu(openAggMenu === field.id ? null : field.id)
                                  }}
                                >
                                  <MoreVertical className="w-3 h-3 text-gray-400" />
                                </button>
                                {openAggMenu === field.id && (
                                  <div
                                    className="absolute right-0 top-5 z-50 bg-white border rounded-md shadow-lg py-1 w-24"
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                  >
                                    {AGGREGATION_OPTIONS.map(op => (
                                      <button
                                        key={op.value}
                                        className="w-full text-left text-xs px-2 py-1 hover:bg-gray-100 transition-colors whitespace-nowrap"
                                        onMouseDown={(e) => {
                                          e.stopPropagation()
                                          e.preventDefault()
                                          onAddComputedMeasure(field.id, op.value)
                                          setOpenAggMenu(null)
                                        }}
                                      >
                                        {op.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {/* 计算字段列表 */}
                          {fieldComputed.map(cm => {
                            const cmSelected = selectedFields.includes(cm.id)
                            return (
                              <div
                                key={cm.id}
                                className={cn(
                                  "flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors text-xs group/field ml-4",
                                  cmSelected
                                    ? "bg-purple-50 text-purple-700"
                                    : "text-gray-500 hover:bg-gray-50"
                                )}
                                onClick={(e) => {
                                  e.preventDefault()
                                  onToggleField(cm.id)
                                }}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('text/plain', `cm-${cm.id}`)
                                  e.dataTransfer.effectAllowed = 'copy'
                                }}
                                title="拖拽到条件筛选区添加筛选条件"
                              >
                                <div
                                  className={cn(
                                    "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                                    cmSelected
                                      ? "bg-purple-500 border-purple-500"
                                      : "border-gray-300"
                                  )}
                                >
                                  {cmSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <GripVertical className="w-3 h-3 text-gray-300 shrink-0 opacity-0 group-hover/field:opacity-100 transition-opacity cursor-grab" />
                                <span className="truncate flex-1 min-w-0">{cm.name}</span>
                                <button
                                  className="text-gray-300 hover:text-red-400 shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onRemoveComputedMeasure(cm.id)
                                  }}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          {/* 计算字段区域（仅透视分析模式） */}
          {isPivotMode && (
          <div>
            <button
              className="w-full flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                onClick={() => onAddCalculatedField({
                  id: `calc-${Date.now()}`,
                  name: '新计算字段',
                  expression: [],
                })}
              >
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="font-medium">计算字段</span>
                <span className="ml-auto text-[10px] text-gcb-primary hover:text-gcb-primaryDark transition-colors">+ 新增</span>
              </button>
              <div className="ml-4 space-y-0.5 pb-1">
                {calculatedFields.map(cf => {
                  const isSelected = selectedFields.includes(cf.id)
                  const exprText = cf.expression.map((node, i) => {
                    if (node.type === 'field') {
                      const f = fields.find(ff => ff.id === node.fieldId)
                      const aggLabel = AGGREGATION_OPTIONS.find(o => o.value === node.aggType)?.label || ''
                      return f ? `${f.name}_${aggLabel}` : node.fieldId
                    }
                    if (node.type === 'operator') return ` ${node.value} `
                    if (node.type === 'number') return node.value
                    if (node.type === 'paren') return node.value === '(' ? '(' : ')'
                    return ''
                  }).join('')
                  const isCalcField = cf.id.startsWith('calc-')
                  return (
                    <div key={cf.id} className="relative group/calc">
                      <div
                        className={cn(
                          "flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors text-xs group/field",
                          isSelected
                            ? "bg-amber-50 text-amber-700"
                            : "text-gray-500 hover:bg-gray-50"
                        )}
                        onClick={(e) => {
                          e.preventDefault()
                          onToggleField(cf.id)
                        }}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `calculated-${cf.id}`)
                          e.dataTransfer.effectAllowed = 'copy'
                        }}
                        title="拖拽到条件筛选区添加筛选条件"
                      >
                        <div
                          className={cn(
                            "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all",
                            isSelected
                              ? "bg-amber-500 border-amber-500"
                              : "border-gray-300"
                          )}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <GripVertical className="w-3 h-3 text-gray-300 shrink-0 opacity-0 group-hover/field:opacity-100 transition-opacity cursor-grab" />
                        <span className="truncate flex-1 min-w-0 text-amber-700">{cf.name}</span>
                        <button
                          className="p-0.5 rounded hover:bg-gray-200 transition-colors shrink-0"
                          onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            onUpdateCalculatedField(cf)
                          }}
                          title="编辑表达式"
                        >
                          <Edit3 className="w-3 h-3 text-gray-400" />
                        </button>
                        <button
                          className="text-gray-300 hover:text-red-400 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRemoveCalculatedField(cf.id)
                          }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      {/* 表达式悬浮提示（仅鼠标悬停时显示） */}
                      {exprText && (
                        <div className="absolute left-full top-0 ml-1 z-50 invisible opacity-0 group-hover/calc:visible group-hover/calc:opacity-100 transition-opacity pointer-events-none">
                          <div className="bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap shadow-lg">
                            {exprText}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
                </div>
          </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

/* ---- 工具栏 ---- */
interface ToolbarProps {
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onRefresh: () => void
  onSave: () => void
  onExport: () => void
  isBrowseMode: boolean
  onToggleBrowseMode: () => void
  isPivotMode: boolean
  onTogglePivotMode: () => void
  onSubmitQuestion: () => void
}

function Toolbar({ canUndo, canRedo, onUndo, onRedo, onRefresh, onSave, onExport, isBrowseMode, onToggleBrowseMode, isPivotMode, onTogglePivotMode, onSubmitQuestion }: ToolbarProps) {
  const toolbarItems = [
    { icon: <Undo2 className="w-3.5 h-3.5" />, label: '撤销', disabled: !canUndo, onClick: onUndo },
    { icon: <Redo2 className="w-3.5 h-3.5" />, label: '重做', disabled: !canRedo, onClick: onRedo },
    { icon: <RefreshCw className="w-3.5 h-3.5" />, label: '刷新', onClick: onRefresh },
    { icon: <Save className="w-3.5 h-3.5" />, label: '保存', onClick: onSave },
    { icon: <Download className="w-3.5 h-3.5" />, label: '导出', onClick: onExport },
    {
      icon: isBrowseMode ? <Edit3 className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />,
      label: isBrowseMode ? '编辑模式' : '浏览模式',
      active: isBrowseMode,
      onClick: onToggleBrowseMode,
    },
    {
      icon: isPivotMode ? <Table2 className="w-3.5 h-3.5" /> : <PieChart className="w-3.5 h-3.5" />,
      label: isPivotMode ? '明细查询' : '透视分析',
      onClick: onTogglePivotMode,
    },
  ]

  return (
    <div className="h-10 border-b bg-white flex items-center px-3 gap-1">
      {toolbarItems.map((item) => (
        <button
          key={item.label}
          className={cn(
            "flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-colors",
            (item as any).active
              ? ((item as any).activeClass || "bg-blue-100 text-gcb-primary font-medium")
              : "text-gray-600 hover:bg-gray-100",
            item.disabled && "text-gray-300 cursor-not-allowed hover:bg-transparent"
          )}
          disabled={item.disabled}
          title={item.label}
          onClick={item.onClick}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
      {/* 提交问题按钮 */}
      <button
        onClick={onSubmitQuestion}
        className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition-colors text-gray-600 hover:bg-gray-100 ml-auto"
        title="提交问题"
      >
        <Headphones className="w-3.5 h-3.5" />
        <span>提交问题</span>
      </button>
    </div>
  )
}

/* ---- 表格 ---- */
interface DataTableProps {
  data: Record<string, string>[]
  selectedFields: string[]
  computedMeasures: ComputedMeasure[]
  calculatedFields?: CalculatedField[]
  fields: Field[]
  allData?: Record<string, string>[]
}

function DataTable({ data, selectedFields, computedMeasures, calculatedFields = [], fields, allData }: DataTableProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const timeData = allData || data  // 时间对比用全量数据
  // 基础字段列（优先使用预定义列）
  const baseColumns = FIELD_COLUMNS.filter(col => selectedFields.includes(col.key))
  // 动态列：selectedFields 中不在 FIELD_COLUMNS 的字段
  const definedKeys = new Set(FIELD_COLUMNS.map(c => c.key))
  const dynamicFields = selectedFields.filter(id => !definedKeys.has(id) && !id.startsWith('cm-') && !id.startsWith('calc-'))
  const dynamicColumns = dynamicFields.map(id => {
    const field = fields.find(f => f.id === id)
    if (!field) return null
    const label = field.dataType ? `${field.name}(${field.dataType})` : field.name
    const config = field.type === 'measure'
      ? { width: '130px', align: 'right' as const, mono: true }
      : { width: '130px', align: 'left' as const, mono: false }
    return { key: id, label, ...config }
  }).filter(Boolean) as Array<{ key: string; label: string; width: string; align: 'left' | 'right'; mono?: boolean }>
  // 计算字段列（透视分析聚合）
  const activeCm = computedMeasures.filter(c => selectedFields.includes(c.id))
  const cmColumns = activeCm.map(c => ({
    key: c.id,
    label: c.name,
    width: '130px',
    align: 'right' as const,
    mono: true,
  }))
  // 自定义计算字段列
  const activeCalc = calculatedFields.filter(c => selectedFields.includes(c.id))
  const calcColumns = activeCalc.map(c => ({
    key: c.id,
    label: c.name,
    width: '130px',
    align: 'right' as const,
    mono: true,
  }))
  const visibleColumns = [...baseColumns, ...dynamicColumns, ...cmColumns, ...calcColumns]

  // 数据变化时重置页码
  useEffect(() => { setCurrentPage(1) }, [data])

  // 分页计算
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const startIndex = (safePage - 1) * pageSize
  const pageData = data.slice(startIndex, startIndex + pageSize)

  // 构建时间对比查找表（明细模式）
  const timeComparisonCm = computedMeasures.filter(c =>
    ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(c.aggType)
  )
  const dateFieldId = fields.find(f => f.type === 'dimension' && f.dataType === 'date')?.id || 'd1'
  const hasDateCol = data.length > 0 && dateFieldId in data[0]
  const dateSourceMap: Record<string, Record<string, number>> = {}  // date -> sourceFieldId -> sum
  if (hasDateCol && timeComparisonCm.length > 0) {
    for (const row of timeData) {
      if (!row) continue
      const date = row[dateFieldId as keyof typeof row] as string
      if (!dateSourceMap[date]) dateSourceMap[date] = {}
      for (const cm of timeComparisonCm) {
        const src = row[cm.sourceFieldId] as string
        dateSourceMap[date][cm.sourceFieldId] = (dateSourceMap[date][cm.sourceFieldId] || 0) + parseNum(src || '0')
      }
    }
  }
  const sortedDates = Object.keys(dateSourceMap).sort()

  const getRefDateVal = (date: string, sourceFieldId: string, type: string): number | null => {
    const idx = sortedDates.indexOf(date)
    if (idx < 0) return null
    let refIdx = -1
    switch (type) {
      case 'vsPrevDay': {
        // 比上日：查找日历上恰好前一天的数据
        const [y, m, d] = date.split('-').map(Number)
        const prev = new Date(y, m - 1, d - 1)
        const formatted = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`
        refIdx = sortedDates.indexOf(formatted)
        break
      }
      case 'vsPrevMonth': {
        // 比上月：查找上个月的数据
        const [y, m] = date.split('-').map(Number)
        const prevMonth = m === 1 ? 12 : m - 1
        const prevYear = m === 1 ? y - 1 : y
        const target = `${prevYear}-${String(prevMonth).padStart(2, '0')}`
        for (let i = idx - 1; i >= 0; i--) {
          if (sortedDates[i].startsWith(target)) { refIdx = i; break }
        }
        break
      }
      case 'vsPrevQuarter': {
        const [y, m] = date.split('-').map(Number)
        const q = Math.ceil(m / 3)
        for (let i = idx - 1; i >= 0; i--) {
          const [ry, rm] = sortedDates[i].split('-').map(Number)
          const rq = Math.ceil(rm / 3)
          if (ry < y || rq < q) { refIdx = i; break }
        }
        break
      }
      case 'vsYearStart': {
        // 比年初：与上年末（12月31日）的值对比
        const [y] = date.split('-').map(Number)
        const prevYearEnd = `${y - 1}-12-31`
        refIdx = sortedDates.indexOf(prevYearEnd)
        break
      }
    }
    if (refIdx < 0) return null
    return dateSourceMap[sortedDates[refIdx]]?.[sourceFieldId] ?? null
  }

  // 渲染单元格的值
  const getCellValue = (row: Record<string, string>, colKey: string) => {
    if (!row) return '-'
    // 先查基础字段
    const rawVal = row[colKey] as string
    if (rawVal !== undefined) return rawVal
    // 查计算字段（透视聚合）
    const cm = computedMeasures.find(c => c.id === colKey)
    if (cm) {
      const isTimeComparison = ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(cm.aggType)
      if (isTimeComparison) {
        const date = row[dateFieldId as keyof typeof row] as string
        const refVal = date ? getRefDateVal(date, cm.sourceFieldId, cm.aggType) : null
        const currentVal = parseNum(row[cm.sourceFieldId] as string || '0')
        if (refVal !== null) return formatNum(currentVal - refVal)
        return '-'
      }
      return row[cm.sourceFieldId] as string
    }
    // 查自定义计算字段
    const cf = calculatedFields.find(c => c.id === colKey)
    if (cf && cf.expression.length > 0) {
      // 明细模式：每行直接取原始字段值，不按聚合类型区分
      const measureValues: Record<string, number> = {}
      for (const node of cf.expression) {
        if (node.type === 'field') {
          const raw = row[node.fieldId] as string
          if (raw !== undefined && raw !== '') {
            const num = parseNum(raw)
            // 明细模式下所有聚合类型都用同一个原始值
            measureValues[`${node.fieldId}_sum`] = num
            measureValues[`${node.fieldId}_avg`] = num
            measureValues[`${node.fieldId}_max`] = num
            measureValues[`${node.fieldId}_min`] = num
          }
        }
      }
      const val = evaluateExpression(cf.expression, measureValues)
      return val !== null ? formatNum(val) : '-'
    }
    return ''
  }

  if (visibleColumns.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm bg-white">
        请在右侧数据面板选择要显示的字段
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-gradient-to-r from-blue-500 to-blue-600">
              <th className="border border-blue-400/30 px-3 py-2 text-left font-medium text-white sticky top-0 w-10 text-center">序号</th>
              {visibleColumns.map(col => (
                <th
                  key={col.key}
                  className="border border-blue-400/30 px-3 py-2 text-left font-medium text-white sticky top-0 whitespace-nowrap"
                  style={{ width: col.width, minWidth: col.width, textAlign: col.align }}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    <ArrowUpDown className="w-3 h-3 opacity-50" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, index) => (
              <tr key={startIndex + index} className="hover:bg-blue-50/30 transition-colors">
                <td className="border border-gray-200 px-3 py-2 text-center text-gray-500">{startIndex + index + 1}</td>
                {visibleColumns.map(col => {
                  const val = getCellValue(row, col.key)
                  const isTimeCmCol = !!computedMeasures.find(c => c.id === col.key && ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(c.aggType))
                  const isF7Col = col.key === 'f7'
                  const isTimeComparisonCol = isTimeCmCol || isF7Col
                  const isPositive = isTimeComparisonCol && (val.startsWith('+') || (!val.startsWith('-') && val !== '-' && !isNaN(parseFloat(val.replace(/,/g, '')))))
                  const isNegative = isTimeComparisonCol && val.startsWith('-')
                const isNewTodayCol = col.key.endsWith('_new_today')
                const isNewTodayPositive = isNewTodayCol && val.startsWith('+')
                const isNewTodayNonPositive = isNewTodayCol && (val.startsWith('-') || val === '0')
                return (
                  <td
                    key={col.key}
                    className={cn(
                      "border border-gray-200 px-3 py-2 whitespace-nowrap",
                      col.align === 'right' ? "text-right font-mono" : "text-left"
                    )}
                  >
                    <span className={cn(
                      isPositive && "text-red-600",
                      isNegative && "text-green-600",
                      isNewTodayPositive && "text-red-600",
                      isNewTodayNonPositive && "text-green-600"
                    )}>
                      {val}
                    </span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {/* 分页栏 */}
      <div className="h-9 border-t bg-gray-50 flex items-center justify-between px-4 text-xs text-gray-500 shrink-0">
        <div className="flex items-center gap-2">
          <span>共 {data.length} 条记录</span>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
            className="border rounded px-1 py-0.5 bg-white hover:bg-gray-100 focus:outline-none"
          >
            {[10, 20, 50, 100].map(size => (
              <option key={size} value={size}>{size}条/页</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button
            className="px-2 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={safePage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >
            上一页
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let page: number
            if (totalPages <= 5) {
              page = i + 1
            } else if (safePage <= 3) {
              page = i + 1
            } else if (safePage >= totalPages - 2) {
              page = totalPages - 4 + i
            } else {
              page = safePage - 2 + i
            }
            return (
              <button
                key={page}
                className={`px-2 py-1 rounded border ${page === safePage ? 'bg-gcb-primary text-white' : 'bg-white hover:bg-gray-100'}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            )
          })}
          <button
            className="px-2 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={safePage >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---- 透视表格 ---- */
interface PivotTableProps {
  data: Record<string, string>[]
  allSelectedFields: string[]
  computedFields: ComputedMeasure[]
  calculatedFields?: CalculatedField[]
  fields: Field[]
  pageSize?: number
}

function PivotTable({ data, allSelectedFields, computedFields, calculatedFields = [], fields, pageSize = 10 }: PivotTableProps) {
  const [currentPage, setCurrentPage] = useState(1)

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm bg-white">
        暂无数据
      </div>
    )
  }

  // 构建列：维度字段 + 计算字段 + 自定义计算字段
  const dimFields = allSelectedFields.filter(f => fields.find(rf => rf.id === f)?.type === 'dimension')
  const columns = [...dimFields, ...computedFields.map(c => c.id), ...calculatedFields.filter(c => allSelectedFields.includes(c.id)).map(c => c.id)]

  // 分页计算
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const startIndex = (safePage - 1) * pageSize
  const pageData = data.slice(startIndex, startIndex + pageSize)

  const getColumnLabel = (colKey: string) => {
    const field = fields.find(f => f.id === colKey)
    if (field) return field.name
    const cm = computedFields.find(c => c.id === colKey)
    if (cm) return cm.name
    const cf = calculatedFields.find(c => c.id === colKey)
    if (cf) return cf.name
    return colKey
  }

  return (
    <div className="flex-1 overflow-auto bg-white flex flex-col">
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-gradient-to-r from-purple-500 to-indigo-500">
              <th className="border border-purple-400/30 px-3 py-2 text-left font-medium text-white sticky top-0 w-10 text-center">序号</th>
              {columns.map(col => {
                const isComputed = !!computedFields.find(c => c.id === col)
                return (
                  <th
                    key={col}
                    className={cn(
                      "border border-purple-400/30 px-3 py-2 font-medium text-white sticky top-0 whitespace-nowrap",
                      isComputed ? "bg-purple-500/20 text-right" : "text-left"
                    )}
                  >
                    <div className="flex items-center gap-1">
                      {getColumnLabel(col)}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, index) => (
              <tr key={index} className="hover:bg-purple-50/30 transition-colors">
                <td className="border border-gray-200 px-3 py-2 text-center text-gray-500">{startIndex + index + 1}</td>
                {columns.map(col => {
                  const isComputed = !!computedFields.find(c => c.id === col)
                  const isCalculated = !!calculatedFields.find(c => c.id === col)
                  const val = row[col] || ''
                  // 时间对比列着色（比上日/比上月/比季/比年初 + f7原始字段）
                  const isTimeCm = isComputed && !!computedFields.find(c => c.id === col && ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(c.aggType))
                  const isF7Col = col === 'f7'
                  const isTimeComparisonCol = isTimeCm || isF7Col
                  const isPositive = isTimeComparisonCol && (val.startsWith('+') || (!val.startsWith('-') && val !== '-' && !isNaN(parseFloat(val.replace(/,/g, '')))))
                  const isNegative = isTimeComparisonCol && val.startsWith('-')
                  // 当日新增户数列着色（公司价值户净增）
                  const isNewTodayCol = col.endsWith('_new_today')
                  const isNewTodayPositive = isNewTodayCol && val.startsWith('+')
                  const isNewTodayNonPositive = isNewTodayCol && (val.startsWith('-') || val === '0')
                  return (
                    <td
                      key={col}
                      className={cn(
                        "border border-gray-200 px-3 py-2 whitespace-nowrap",
                        col.align === 'right' ? "text-right font-mono" : "text-left",
                        isCalculated && "bg-amber-50/50"
                      )}
                    >
                      <span className={cn(
                        isPositive && "text-red-600",
                        isNegative && "text-green-600",
                        isNewTodayPositive && "text-red-600",
                        isNewTodayNonPositive && "text-green-600"
                      )}>
                        {val}
                      </span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* 分页栏 */}
      <div className="h-9 border-t bg-gray-50 flex items-center justify-between px-4 text-xs text-gray-500 shrink-0">
        <span>共 {data.length} 条记录，第 {safePage}/{totalPages} 页</span>
        <div className="flex items-center gap-1">
          <button
            className="px-2 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={safePage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >
            上一页
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={cn(
                "px-2 py-1 rounded border",
                page === safePage
                  ? "bg-gcb-primary text-white border-gcb-primary"
                  : "bg-white hover:bg-gray-100"
              )}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="px-2 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={safePage >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  )
}

/* ---- 计算字段表达式构建器弹窗 ---- */
interface CalculatedFieldBuilderProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  field: CalculatedField
  fields: Field[]
  onSave: (field: CalculatedField) => void
}

function CalculatedFieldBuilder({ open, onOpenChange, field, fields, onSave }: CalculatedFieldBuilderProps) {
  const [expression, setExpression] = useState<ExpressionNode[]>(field.expression)
  const [name, setName] = useState(field.name)

  // 同步外部 field 变化
  useEffect(() => {
    setExpression(field.expression)
    setName(field.name)
  }, [field.id, field.expression, field.name])

  const measureFields = fields.filter(f => f.type === 'measure')

  const handleDragStartField = (e: React.DragEvent, fieldId: string) => {
    e.dataTransfer.setData('text/plain', fieldId)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const fieldId = e.dataTransfer.getData('text/plain')
    if (fieldId && measureFields.find(f => f.id === fieldId)) {
      setExpression(prev => [...prev, { type: 'field', fieldId, aggType: 'sum' }])
    }
  }

  const handleAddOperator = (op: '+' | '-' | '*' | '/') => {
    setExpression(prev => [...prev, { type: 'operator', value: op }])
  }

  const handleAddNumber = (num: string) => {
    setExpression(prev => [...prev, { type: 'number', value: num }])
  }

  const handleRemoveNode = (index: number) => {
    setExpression(prev => prev.filter((_, i) => i !== index))
  }

  const handleNodeAggChange = (index: number, aggType: AggregationType) => {
    setExpression(prev => prev.map((node, i) => {
      if (i === index && node.type === 'field') {
        return { ...node, aggType }
      }
      return node
    }))
  }

  const handleSave = () => {
    if (!name.trim() || expression.length === 0) return
    onSave({ ...field, name: name.trim(), expression })
    onOpenChange(false)
  }

  const expressionPreview = expression.map((node, i) => {
    if (node.type === 'field') {
      const f = fields.find(ff => ff.id === node.fieldId)
      const aggLabel = AGGREGATION_OPTIONS.find(o => o.value === node.aggType)?.label || ''
      return f ? `${f.name}_${aggLabel}` : node.fieldId
    }
    if (node.type === 'operator') return ` ${node.value} `
    if (node.type === 'number') return node.value
    if (node.type === 'paren') return node.value === '(' ? ' (' : ') '
    return ''
  }).join('')

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => onOpenChange(false)} />
      <div className="relative bg-white rounded-xl shadow-2xl w-[700px] max-h-[80vh] flex flex-col">
        <div className="px-5 py-3 border-b">
          <h3 className="text-sm font-semibold text-gray-900">计算字段表达式</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* 名称输入 */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">字段名称</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="例如：代发留存比例"
              className="h-8 text-sm"
            />
          </div>

          {/* 表达式构建区 */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">
              表达式 <span className="text-gray-400 font-normal">（拖拽字段到此处，或点击下方运算符）</span>
            </label>
            <div
              className={cn(
                  "min-h-[56px] max-h-[100px] border-2 border-dashed rounded-lg p-3 flex items-center gap-1 transition-colors overflow-x-auto",
                  expression.length === 0
                    ? "border-gray-300 bg-gray-50"
                    : "border-gcb-primary/30 bg-blue-50/30"
                )}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
              >
                {expression.length === 0 && (
                  <span className="text-xs text-gray-400">拖拽左侧度量字段到此处</span>
                )}
                {expression.map((node, i) => (
                  <div key={i} className="flex items-center gap-1 shrink-0">
                  {node.type === 'field' && (() => {
                    const f = fields.find(ff => ff.id === node.fieldId)
                    return (
                      <div className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                        <span className="truncate max-w-[120px]">{f?.name || node.fieldId}</span>
                        <select
                          value={node.aggType}
                          onChange={e => handleNodeAggChange(i, e.target.value as AggregationType)}
                          className="text-[10px] bg-white border border-blue-200 rounded px-1 py-0.5 focus:outline-none"
                          onMouseDown={e => e.stopPropagation()}
                        >
                          {AGGREGATION_OPTIONS.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                        <button
                          className="text-blue-400 hover:text-red-500 ml-0.5 shrink-0"
                          onMouseDown={e => { e.stopPropagation(); handleRemoveNode(i) }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )
                  })()}
                  {node.type === 'operator' && (
                    <div className="flex items-center gap-0.5 bg-gray-100 px-2 py-1 rounded text-xs">
                      <span className="font-bold text-gcb-primary">{node.value}</span>
                      <button
                        className="text-gray-400 hover:text-red-500 shrink-0"
                        onMouseDown={e => { e.stopPropagation(); handleRemoveNode(i) }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {node.type === 'number' && (
                    <div className="flex items-center gap-0.5 bg-amber-50 text-amber-800 px-2 py-1 rounded text-xs font-mono">
                      <Input
                        value={node.value}
                        onChange={e => {
                          setExpression(prev => prev.map((n, idx) =>
                            idx === i ? { ...n, value: e.target.value } : n
                          ))
                        }}
                        className="h-5 w-16 text-xs px-1 bg-transparent border-0 focus:ring-0"
                        onMouseDown={e => e.stopPropagation()}
                      />
                      <button
                        className="text-amber-400 hover:text-red-500 shrink-0"
                        onMouseDown={e => { e.stopPropagation(); handleRemoveNode(i) }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {node.type === 'paren' && (
                    <div className="flex items-center gap-0.5 bg-purple-50 text-purple-700 px-1.5 py-1 rounded text-xs font-bold">
                      <span>{node.value}</span>
                      <button
                        className="text-purple-400 hover:text-red-500 shrink-0"
                        onMouseDown={e => { e.stopPropagation(); handleRemoveNode(i) }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 快捷添加区域 */}
          <div className="space-y-2">
            {/* 运算符和括号 */}
            <div className="space-y-1">
              <span className="text-[10px] text-gray-500">运算符（点击添加到表达式末尾）</span>
              <div className="flex gap-1.5">
                {(['+', '-', '*', '/'] as const).map(op => (
                  <button
                    key={op}
                    onClick={() => handleAddOperator(op)}
                    className="w-8 h-8 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gcb-primary hover:bg-blue-50 hover:border-gcb-primary/30 transition-all"
                  >
                    {op}
                  </button>
                ))}
                <button
                  onClick={() => setExpression(prev => [...prev, { type: 'paren', value: '(' }])}
                  className="w-8 h-8 rounded-lg border border-gray-200 bg-white text-sm font-bold text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all"
                >
                  (
                </button>
                <button
                  onClick={() => setExpression(prev => [...prev, { type: 'paren', value: ')' }])}
                  className="w-8 h-8 rounded-lg border border-gray-200 bg-white text-sm font-bold text-purple-600 hover:bg-purple-50 hover:border-purple-300 transition-all"
                >
                  )
                </button>
              </div>
            </div>

            {/* 度量字段拖拽区 */}
            <div className="space-y-1">
              <span className="text-[10px] text-gray-500">度量字段（拖拽到表达式区）</span>
              <div className="grid grid-cols-2 gap-1.5">
                {measureFields.map(f => (
                  <div
                    key={f.id}
                    draggable
                    onDragStart={e => handleDragStartField(e, f.id)}
                    className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs cursor-grab hover:bg-blue-50 hover:border-gcb-primary/30 transition-colors select-none"
                  >
                    <GripVertical className="w-3 h-3 text-gray-300 shrink-0" />
                    <span className="truncate">{f.name}</span>
                    {f.dataType && <span className="text-[10px] text-gray-400 shrink-0">{f.dataType}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 表达式预览 */}
          {expressionPreview && (
            <div className="space-y-1">
              <span className="text-[10px] text-gray-500">表达式预览</span>
              <div className="bg-gray-50 border rounded-lg px-3 py-2 text-xs font-mono text-gray-700">
                {expressionPreview}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            size="sm"
            className="text-xs bg-gcb-primary hover:bg-gcb-primaryDark"
            onClick={handleSave}
            disabled={!name.trim() || expression.length === 0}
          >
            保存
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ---- 主组件 ---- */

/** 保存报表的参数 */
export interface SaveReportParams {
  name: string
  description: string
  categoryId: string
  /** 报表配置（字段、筛选、模式等） */
  config: {
    selectedFields: string[]
    filters: FilterCondition[]
    isPivotMode: boolean
    computedMeasures: ComputedMeasure[]
    calculatedFields: CalculatedField[]
    activeReportTemplate: string | null | undefined
  }
}

interface ReportBuilderProps {
  asset: DataAsset | null
  /** 预设勾选的字段 ID 列表。不传则使用默认逻辑（有 asset 全勾选，无 asset 全不勾选） */
  presetFields?: string[]
  /** 预设的筛选条件。不传则使用默认逻辑 */
  presetFilters?: FilterCondition[]
  /** 隐藏的筛选条件（应用到数据但不显示在筛选区域，如支行权限过滤） */
  hiddenFilters?: FilterCondition[]
  /** 是否默认进入透视分析模式 */
  presetPivotMode?: boolean
  /** 当前进入报表制作的模板 ID（如 '1'=对公大额存款, '11'=代发客群营销） */
  activeReportTemplate?: string | null
  /** 预设计算字段（透视分析模式下默认勾选的聚合字段） */
  presetComputedMeasures?: Array<{ sourceFieldId: string; aggType: AggregationType }>
  /** 预设自定义计算字段（表达式字段，如转出比例） */
  presetCalculatedFields?: CalculatedField[]
  /** 保存报表回调 */
  onSaveReport?: (params: SaveReportParams) => void
  /** 保存成功后的回调（用于关闭页面等） */
  onSaveSuccess?: () => void
}

interface HistoryState {
  selectedFields: string[]
  filters: FilterCondition[]
}

export function ReportBuilder({ asset, presetFields, presetFilters, hiddenFilters, presetPivotMode, activeReportTemplate, presetComputedMeasures, presetCalculatedFields, onSaveReport, onSaveSuccess }: ReportBuilderProps) {
  // 根据模板 ID 选择字段和数据源
  const isPayroll = activeReportTemplate === '11'
  const isCorpBranchCorp = activeReportTemplate === '12a'
  const isCorpBranchPersonal = activeReportTemplate === '12b'
  const isCorpBranch = activeReportTemplate === '12'
  const isInclusiveLoan = activeReportTemplate === '101'
  const isCorpCustomer = activeReportTemplate === '102'
  const isCorpNewCustomer = activeReportTemplate === '104'
  const isPersonalCustomerDetail = activeReportTemplate === '106'
  const currentFields = isPayroll ? PAYROLL_REPORT_FIELDS : isCorpBranchCorp ? CORP_BRANCH_CORP_FIELDS : isCorpBranchPersonal ? CORP_BRANCH_PERSONAL_FIELDS : isCorpBranch ? CORP_BRANCH_REPORT_FIELDS : isInclusiveLoan ? INCLUSIVE_LOAN_FIELDS : isCorpCustomer ? CORP_CUSTOMER_FIELDS : isCorpNewCustomer ? CORP_NEW_CUSTOMER_FIELDS : isPersonalCustomerDetail ? PERSONAL_CUSTOMER_DETAIL_FIELDS : REPORT_FIELDS
  const currentTableData = isPayroll ? PAYROLL_TABLE_DATA : isCorpBranchCorp || isCorpBranchPersonal || isCorpBranch ? CORP_BRANCH_TABLE_DATA : isInclusiveLoan ? INCLUSIVE_LOAN_TABLE_DATA : isCorpCustomer ? CORP_CUSTOMER_TABLE_DATA : isCorpNewCustomer ? CORP_NEW_CUSTOMER_TABLE_DATA : isPersonalCustomerDetail ? PERSONAL_CUSTOMER_DETAIL_DATA : TABLE_DATA

  // 字段默认值：如果传入了 presetFields 则使用它（过滤掉当前模板不存在的字段）；否则有 asset 时全选，无 asset 时不选
  const resolvedFields = presetFields !== undefined
    ? presetFields.filter(id => currentFields.some(f => f.id === id))
    : (asset ? currentFields.map(f => f.id) : [])
  // 预设筛选：过滤掉当前模板不存在的字段（保留内置字段、计算字段 calc-*、计算度量 cm-*）
  const resolvedFilters = presetFilters !== undefined
    ? presetFilters.filter(f =>
        currentFields.some(cf => cf.id === f.field.id)
        || f.field.id.startsWith('calc-')
        || f.field.id.startsWith('cm-')
      )
    : (asset ? [{ id: 'filter-default', field: currentFields.find(f => f.id === 'f7')!, operator: 'gte', value: '1000' }] : [])

  // 预设计算字段：生成稳定的 ID 列表
  const resolvedComputedMeasures: ComputedMeasure[] = presetComputedMeasures
    ? presetComputedMeasures.map(cm => {
        const sourceField = currentFields.find(f => f.id === cm.sourceFieldId)
        const aggLabel = AGGREGATION_OPTIONS.find(o => o.value === cm.aggType)?.label || cm.aggType
        const baseName = sourceField
          ? (sourceField.dataType ? `${sourceField.name}(${sourceField.dataType})` : sourceField.name)
          : cm.sourceFieldId
        return {
          id: `cm-${cm.sourceFieldId}-${cm.aggType}`,
          sourceFieldId: cm.sourceFieldId,
          name: `${baseName}_${aggLabel}`,
          aggType: cm.aggType,
        }
      })
    : []

  const resolvedCalculatedFields: CalculatedField[] = presetCalculatedFields ?? []

  const [selectedFields, setSelectedFields] = useState<string[]>([
    ...resolvedFields,
    ...resolvedComputedMeasures.map(cm => cm.id),
    ...resolvedCalculatedFields.map(cf => cf.id),
  ])
  const [filters, setFilters] = useState<FilterCondition[]>(resolvedFilters)
  const [isBrowseMode, setIsBrowseMode] = useState(true)
  const [computedMeasures, setComputedMeasures] = useState<ComputedMeasure[]>(resolvedComputedMeasures)
  const [calculatedFields, setCalculatedFields] = useState<CalculatedField[]>(resolvedCalculatedFields)

  // isPivotMode 受控于 presetPivotMode：用 key 保证 remount 时重新初始化
  const [isPivotMode, setIsPivotMode] = useState(presetPivotMode || false)
  // 使用 key 变化来追踪 presetPivotMode 变化（配合 reportKey 从父组件 remount）
  const isPivotModeKey = useRef<boolean>(presetPivotMode || false)
  if (isPivotModeKey.current !== presetPivotMode) {
    isPivotModeKey.current = presetPivotMode
    setIsPivotMode(presetPivotMode || false)
    if (!presetPivotMode) {
      setComputedMeasures([])
      setSelectedFields(prev => prev.filter(id => !id.startsWith('cm-')))
    }
  }

  // 模板/预设参数切换时同步字段和筛选状态
  useEffect(() => {
    setSelectedFields([
      ...resolvedFields,
      ...resolvedComputedMeasures.map(cm => cm.id),
      ...resolvedCalculatedFields.map(cf => cf.id),
    ])
    setFilters(resolvedFilters)
    setComputedMeasures(resolvedComputedMeasures)
    setCalculatedFields(resolvedCalculatedFields)
  }, [activeReportTemplate, presetFields, presetFilters, presetComputedMeasures, presetCalculatedFields]) // eslint-disable-line react-hooks/exhaustive-deps

  // 添加计算字段
  const handleAddComputedMeasure = (sourceFieldId: string, aggType: AggregationType) => {
    const sourceField = currentFields.find(f => f.id === sourceFieldId)
    if (!sourceField) return
    // 避免重复添加相同聚合类型
    const exists = computedMeasures.find(c => c.sourceFieldId === sourceFieldId && c.aggType === aggType)
    if (exists) return
    const aggLabel = AGGREGATION_OPTIONS.find(o => o.value === aggType)?.label || aggType
    const baseName = sourceField.dataType ? `${sourceField.name}(${sourceField.dataType})` : sourceField.name
    const id = `cm-${sourceFieldId}-${aggType}`
    const name = `${baseName}_${aggLabel}`
    const newMeasure: ComputedMeasure = { id, sourceFieldId, name, aggType }
    setComputedMeasures(prev => [...prev, newMeasure])
    // 自动勾选并加入选中字段（确保不重复）
    setSelectedFields(prev => {
      if (prev.includes(id)) return prev
      return [...prev, id]
    })
  }

  const handleRemoveComputedMeasure = (id: string) => {
    setComputedMeasures(prev => prev.filter(c => c.id !== id))
    setSelectedFields(prev => prev.filter(f => f !== id))
  }

  // 自定义计算字段操作
  const handleAddCalculatedField = (field: CalculatedField) => {
    setEditingCalcField(field)
    setShowCalcBuilder(true)
  }

  const handleSaveCalculatedField = (field: CalculatedField) => {
    const exists = calculatedFields.find(c => c.id === field.id)
    if (exists) {
      setCalculatedFields(prev => prev.map(c => c.id === field.id ? field : c))
    } else {
      setCalculatedFields(prev => [...prev, field])
      // 自动勾选
      setSelectedFields(prev => prev.includes(field.id) ? prev : [...prev, field.id])
    }
    setEditingCalcField(null)
    setShowCalcBuilder(false)
  }

  const handleRemoveCalculatedField = (id: string) => {
    setCalculatedFields(prev => prev.filter(c => c.id !== id))
    setSelectedFields(prev => prev.filter(f => f !== id))
  }

  // 撤销/重做历史栈
  const [history, setHistory] = useState<HistoryState[]>([])
  const [future, setFuture] = useState<HistoryState[]>([])

  // 自定义计算字段 builder 状态
  const [showCalcBuilder, setShowCalcBuilder] = useState(false)
  const [editingCalcField, setEditingCalcField] = useState<CalculatedField | null>(null)

  // 提交问题弹窗状态
  const [showServiceDialog, setShowServiceDialog] = useState(false)
  const [serviceQuestion, setServiceQuestion] = useState('')

  // 字段悬浮提示状态（2秒自动消失）
  const [hoveredField, setHoveredField] = useState<{ id: string; name: string; rect: DOMRect | null } | null>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleHoverField = useCallback((field: { id: string; name: string; rect: DOMRect | null } | null) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    setHoveredField(field)
    if (field) {
      hoverTimerRef.current = setTimeout(() => setHoveredField(null), 2000)
    }
  }, [])

  useEffect(() => {
    return () => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current) }
  }, [])

  const handleServiceSubmit = () => {
    if (!serviceQuestion.trim()) return
    // 模拟提交成功
    console.log('提交问题:', serviceQuestion)
    setServiceQuestion('')
    setShowServiceDialog(false)
  }

  const [showAIChatModal, setShowAIChatModal] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  /* ---- 保存弹窗状态 ---- */
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saveDescription, setSaveDescription] = useState('')
  const [saveCategoryId, setSaveCategoryId] = useState('')
  const [saveError, setSaveError] = useState('')
  const [showSaveSuccess, setShowSaveSuccess] = useState(false)
  const [savedReportName, setSavedReportName] = useState('')

  const REPORT_CATEGORIES = [
    { id: 'marketing', name: '客户营销' },
    { id: 'assessment', name: '考核跟踪' },
  ]

  const handleSave = () => {
    // 打开保存弹窗
    const topicName = displayTopicName || '报表'
    setSaveName(topicName)
    setSaveDescription('')
    setSaveCategoryId('')
    setSaveError('')
    setShowSaveDialog(true)
  }

  const handleConfirmSave = () => {
    if (!saveName.trim()) {
      setSaveError('请输入报表名称')
      return
    }
    if (!saveCategoryId) {
      setSaveError('请选择报表目录')
      return
    }
    onSaveReport?.({
      name: saveName.trim(),
      description: saveDescription.trim() || displayTopicName || '自定义报表',
      categoryId: saveCategoryId,
      config: {
        selectedFields: [...selectedFields],
        filters: [...filters],
        isPivotMode,
        computedMeasures: [...computedMeasures],
        calculatedFields: [...calculatedFields],
        activeReportTemplate,
      },
    })
    // 显示成功反馈，延迟关闭
    setSavedReportName(saveName.trim())
    setShowSaveDialog(false)
    setShowSaveSuccess(true)
    setTimeout(() => {
      setShowSaveSuccess(false)
      onSaveSuccess?.()
    }, 1500)
  }

  // 记录历史快照
  const pushHistory = useCallback(() => {
    setHistory(prev => [...prev, { selectedFields: [...selectedFields], filters: [...filters] }])
    setFuture([])
  }, [selectedFields, filters])

  const handleUndo = useCallback(() => {
    if (history.length === 0) return
    const prev = history[history.length - 1]
    setFuture(f => [...f, { selectedFields: [...selectedFields], filters: [...filters] }])
    setSelectedFields(prev.selectedFields)
    setFilters(prev.filters)
    setHistory(h => h.slice(0, -1))
  }, [history, selectedFields, filters])

  const handleRedo = useCallback(() => {
    if (future.length === 0) return
    const next = future[future.length - 1]
    setHistory(h => [...h, { selectedFields: [...selectedFields], filters: [...filters] }])
    setSelectedFields(next.selectedFields)
    setFilters(next.filters)
    setFuture(f => f.slice(0, -1))
  }, [future, selectedFields, filters])

  const handleToggleField = (fieldId: string) => {
    pushHistory()
    setSelectedFields(prev =>
      prev.includes(fieldId) ? prev.filter(id => id !== fieldId) : [...prev, fieldId]
    )
  }

  const handleAddFilter = (field: Field) => {
    if (filters.find(f => f.field.id === field.id)) return
    pushHistory()
    const newFilter: FilterCondition = {
      id: `filter-${Date.now()}`,
      field,
      operator: field.type === 'dimension' ? 'contains' : 'gte',
      value: '',
    }
    setFilters(prev => [...prev, newFilter])
  }

  const handleRemoveFilter = (id: string) => {
    pushHistory()
    setFilters(prev => prev.filter(f => f.id !== id))
  }

  const handleFilterChange = (id: string, patch: Partial<FilterCondition>) => {
    // 输入操作不记录历史（避免每个 keystroke 都快照），只在失去焦点时记录
    setFilters(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f))
  }

  const handleFilterChangeCommit = (_id: string) => {
    pushHistory()
  }

  const handleResetFilters = () => {
    pushHistory()
    setFilters([])
  }

  const handleRefresh = () => {
    // 刷新：重新触发一次查询（与查询效果相同，实际过滤是自动的）
    console.log('刷新：重新查询')
  }

  const handleToggleBrowseMode = () => {
    setIsBrowseMode(prev => !prev)
  }

  // 切换透视模式
  const handleTogglePivotMode = () => {
    if (isPivotMode) {
      // 回到明细模式：清除计算字段
      setComputedMeasures([])
      setSelectedFields(prev => prev.filter(id => !id.startsWith('cm-')))
      setCalculatedFields([])
      setSelectedFields(prev => prev.filter(id => !id.startsWith('calc-')))
    }
    setIsPivotMode(prev => !prev)
  }

  const handleQuery = () => {
    // 查询触发时已自动过滤
  }

  const handleExport = () => {
    // 导出 CSV
    const visibleColumns = FIELD_COLUMNS.filter(col => selectedFields.includes(col.key))
    const headers = visibleColumns.map(col => col.label).join(',')
    const rows = filteredData.map(row =>
      visibleColumns.map(col => {
        const val = row[col.key as keyof typeof row] as string
        return val.includes(',') ? `"${val}"` : val
      }).join(',')
    )
    const csv = [headers, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = '报表数据.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  // 根据筛选条件过滤数据（包含隐藏筛选）
  const allFilters = [...filters, ...(hiddenFilters || [])]

  // 预计算：对每个 cm- 时间对比筛选，按维度组聚合当前值和参考值
  const cmAggCache = new Map<string, Map<string, { currentSum: number; refSum: number; pass: boolean }>>()
  for (const f of allFilters) {
    if (!f.field.id.startsWith('cm-')) continue
    const cm = computedMeasures.find(c => c.id === f.field.id)
    if (!cm) continue
    const isTimeComparison = ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(cm.aggType)
    if (!isTimeComparison) continue

    const dateFieldId = currentFields.find(cf => cf.type === 'dimension' && cf.dataType === 'date')?.id || 'd1'
    const dimIds = currentFields.filter(cf => cf.type === 'dimension' && cf.id !== dateFieldId && cf.id !== 'data_date').map(cf => cf.id)
    const cacheKey = cm.id
    const cache = new Map<string, { currentSum: number; refSum: number; pass: boolean }>()

    // 按维度分组
    const groups = new Map<string, typeof currentTableData>()
    for (const row of currentTableData) {
      const key = dimIds.map(id => row[id as keyof typeof row] || '').join('|||')
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(row)
    }

    for (const [, groupRows] of groups) {
      const dates = [...new Set(groupRows.map(r => r[dateFieldId as keyof typeof r] as string))]
      for (const date of dates) {
        const dateRows = groupRows.filter(r => r[dateFieldId as keyof typeof r] === date)
        const currentSum = dateRows.reduce((s, r) => s + parseNum(r[cm.sourceFieldId] as string), 0)
        const dimKey = dimIds.map(id => dateRows[0][id as keyof typeof dateRows[0]] || '').join('|||')
        const refDate = getRefDate(date, cm.aggType)
        let refSum = 0
        if (refDate) {
          const refDateRows = currentTableData.filter(r => r[dateFieldId as keyof typeof r] === refDate)
          // 找到同维度的参考日期行
          const matchingRefRows = refDateRows.filter(r => {
            for (const dimId of dimIds) {
              if (r[dimId as keyof typeof r] !== dateRows[0][dimId as keyof typeof dateRows[0]]) return false
            }
            return true
          })
          refSum = matchingRefRows.reduce((s, r) => s + parseNum(r[cm.sourceFieldId] as string), 0)
        }
        const diff = currentSum - refSum

        const numFilter = parseFloat(f.value.replace(/[+,]/g, ''))
        let pass = true
        if (!isNaN(numFilter)) {
          switch (f.operator) {
            case 'gte': pass = diff >= numFilter; break
            case 'lte': pass = diff <= numFilter; break
            case 'gt': pass = diff > numFilter; break
            case 'lt': pass = diff < numFilter; break
            case 'eq': pass = diff === numFilter; break
            default: pass = true
          }
        }

        cache.set(`${date}|||${dimKey}`, { currentSum, refSum: refSum, pass })
      }
    }
    cmAggCache.set(cacheKey, cache)
  }

  const filteredData = currentTableData.filter(row => {
    if (allFilters.length === 0) return true
    return allFilters.every(f => {
      // 计算字段筛选：明细模式下先计算表达式值
      if (f.field.id.startsWith('calc-')) {
        const calcField = calculatedFields.find(cf => cf.id === f.field.id)
        if (calcField) {
          const measureValues: Record<string, number> = {}
          for (const node of calcField.expression) {
            if (node.type === 'field') {
              const raw = row[node.fieldId] as string
              if (raw !== undefined && raw !== '') {
                const num = parseNum(raw)
                measureValues[`${node.fieldId}_sum`] = num
                measureValues[`${node.fieldId}_avg`] = num
                measureValues[`${node.fieldId}_max`] = num
                measureValues[`${node.fieldId}_min`] = num
              }
            }
          }
          const val = evaluateExpression(calcField.expression, measureValues)
          if (val === null) return true
          const numFilter = parseFloat(f.value.replace(/[+,]/g, ''))
          if (isNaN(numFilter)) return true
          switch (f.operator) {
            case 'gte': return val >= numFilter
            case 'lte': return val <= numFilter
            case 'gt': return val > numFilter
            case 'lt': return val < numFilter
            case 'eq': return val === numFilter
            default: return true
          }
        }
        return true
      }

      // 汇总值/比上日/比年初等计算字段筛选
      if (f.field.id.startsWith('cm-')) {
        const cm = computedMeasures.find(c => c.id === f.field.id)
        if (cm) {
          const isTimeComparison = ['vsPrevDay', 'vsPrevMonth', 'vsPrevQuarter', 'vsYearStart'].includes(cm.aggType)
          if (isTimeComparison) {
            // 使用预计算的聚合结果
            const cache = cmAggCache.get(cm.id)
            if (cache) {
              const dateFieldId = currentFields.find(cf => cf.type === 'dimension' && cf.dataType === 'date')?.id || 'd1'
              const dimIds = currentFields.filter(cf => cf.type === 'dimension' && cf.id !== dateFieldId && cf.id !== 'data_date').map(cf => cf.id)
              const dimKey = dimIds.map(id => row[id as keyof typeof row] || '').join('|||')
              const cacheKey = `${row[dateFieldId as keyof typeof row]}|||${dimKey}`
              const aggResult = cache.get(cacheKey)
              if (aggResult !== undefined) return aggResult.pass
            }
            return true
          } else if (isPivotMode) {
            // 透视模式下，普通聚合类型（sum/avg/max/min）筛选在聚合后执行，此处跳过
            return true
          } else {
            // 明细模式下直接取源字段值
            const sourceVal = parseNum(row[cm.sourceFieldId] as string)
            const numFilter = parseFloat(f.value.replace(/[+,]/g, ''))
            if (isNaN(numFilter)) return true
            switch (f.operator) {
              case 'gte': return sourceVal >= numFilter
              case 'lte': return sourceVal <= numFilter
              case 'gt': return sourceVal > numFilter
              case 'lt': return sourceVal < numFilter
              case 'eq': return sourceVal === numFilter
              default: return true
            }
          }
        }
        return true
      }

      const rawValue = row[f.field.id as keyof typeof row] as string
      const filterValue = f.value

      // 字符串操作：包含、等于、不等于
      if (f.operator === 'contains') {
        return rawValue.includes(filterValue)
      }
      if (f.operator === 'eq') {
        return rawValue === filterValue
      }
      if (f.operator === 'neq') {
        return rawValue !== filterValue
      }

      // 数值操作：大于、小于等
      const numValue = parseFloat(rawValue.replace(/[+,]/g, ''))
      const numFilter = parseFloat(filterValue.replace(/[+,]/g, ''))
      if (isNaN(numValue) || isNaN(numFilter)) return true

      switch (f.operator) {
        case 'gt': return numValue > numFilter
        case 'lt': return numValue < numFilter
        case 'gte': return numValue >= numFilter
        case 'lte': return numValue <= numFilter
        default: return true
      }
    })
  })

  // 拖拽相关
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const data = e.dataTransfer.getData('text/plain')

    // 计算字段拖拽（标识符以 calculated- 开头）
    if (data.startsWith('calculated-')) {
      const calcFieldId = data.replace('calculated-', '')
      const calcField = calculatedFields.find(f => f.id === calcFieldId)
      if (calcField && !filters.find(f => f.field.id === calcFieldId)) {
        pushHistory()
        const newFilter: FilterCondition = {
          id: `filter-${Date.now()}`,
          field: { id: calcFieldId, name: calcField.name, type: 'measure', group: '计算字段' },
          operator: 'gte',
          value: '',
        }
        setFilters(prev => [...prev, newFilter])
      }
      return
    }

    // 汇总值/比上日/比年初等计算字段拖拽（标识符以 cm- 开头）
    if (data.startsWith('cm-')) {
      const cmId = data.replace('cm-', '')
      const cm = computedMeasures.find(f => f.id === cmId)
      if (cm && !filters.find(f => f.field.id === cmId)) {
        pushHistory()
        const sourceField = currentFields.find(f => f.id === cm.sourceFieldId)
        const newFilter: FilterCondition = {
          id: `filter-${Date.now()}`,
          field: { id: cmId, name: cm.name, type: 'measure', group: sourceField?.group || '计算字段', dataType: sourceField?.dataType },
          operator: 'gte',
          value: '',
        }
        setFilters(prev => [...prev, newFilter])
      }
      return
    }

    // 普通字段拖拽
    const fieldId = data
    const field = currentFields.find(f => f.id === fieldId)
    if (field) {
      handleAddFilter(field)
    }
  }, [handleAddFilter, calculatedFields, computedMeasures, currentFields, filters, pushHistory])

  // 根据模板 ID 显示对应的共享模板名称
  const templateTopicName = activeReportTemplate
    ? (SHARED_TEMPLATES.find(t => t.id === activeReportTemplate)?.name || null)
    : null
  const displayTopicName = asset?.name || templateTopicName
  const displayDepartment = asset?.department
    || (activeReportTemplate ? SHARED_TEMPLATES.find(t => t.id === activeReportTemplate)?.department || null : null)

  // 字段元数据映射（业务定义 + 统计口径）
  const fieldMetadata = useMemo(() => {
    // activeReportTemplate: '11'=代发客群营销 → retail-customer, '1'=对公大额存款 → retail-deposit, '12'=各机构对公客户存款 → corporate-branch-deposit
    let catalogKey: string | null = null
    if (activeReportTemplate === '11') catalogKey = 'retail-customer'
    else if (activeReportTemplate === '1') catalogKey = 'retail-deposit'
    else if (activeReportTemplate === '12' || activeReportTemplate === '12a' || activeReportTemplate === '12b') catalogKey = 'corporate-branch-deposit'
    if (!catalogKey) return {}
    const catalog = CATALOG_DETAILS[catalogKey]
    if (!catalog?.fields) return {}
    const map: Record<string, { definition: string; caliber: string }> = {}
    for (const f of catalog.fields) {
      map[f.id] = { definition: f.definition || '', caliber: f.caliber || '' }
    }
    return map
  }, [activeReportTemplate])

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* 顶部标题 + 工具栏 */}
      <div className="border-b bg-white">
        {displayTopicName && (
          <div className="px-3 py-1.5 border-b bg-gradient-to-r from-blue-50 to-white flex items-center gap-2">
            <Database className="w-4 h-4 text-gcb-primary" />
            <span className="text-sm font-semibold text-gray-900">{displayTopicName}</span>
          </div>
        )}
        {/* 工具栏 */}
        <Toolbar
          canUndo={history.length > 0}
          canRedo={future.length > 0}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onRefresh={handleRefresh}
          onSave={handleSave}
          onExport={handleExport}
          isBrowseMode={isBrowseMode}
          onToggleBrowseMode={handleToggleBrowseMode}
          isPivotMode={isPivotMode}
          onTogglePivotMode={handleTogglePivotMode}
          onSubmitQuestion={() => setShowServiceDialog(true)}
        />
      </div>

      {/* 过滤条件 */}
      <FilterBar
        conditions={filters}
        onAdd={handleAddFilter}
        onRemove={handleRemoveFilter}
        onChange={handleFilterChange}
        onCommitChange={handleFilterChangeCommit}
        onQuery={handleQuery}
        onReset={handleResetFilters}
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      />

      {/* 主体 */}
      <div className="flex flex-1 overflow-hidden">
        {/* 表格区域 */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {isPivotMode ? (
            (() => {
              const activeComputed = computedMeasures.filter(c => selectedFields.includes(c.id))
              const pivotData = computePivotData(filteredData, selectedFields, currentFields, activeComputed, calculatedFields, currentTableData)
              // 透视模式下对计算字段（cm-）和自定义字段（calc-）的筛选在聚合后执行
              const computedFilters = filters.filter(f => f.field.id.startsWith('cm-'))
              const calcFilters = filters.filter(f => f.field.id.startsWith('calc-'))
              const postAggFilters = [...computedFilters, ...calcFilters]
              const filteredPivotData = postAggFilters.length > 0
                ? pivotData.filter(row => {
                    return postAggFilters.every(f => {
                      const valStr = row[f.field.id]
                      if (valStr === undefined || valStr === '-') return true
                      const val = parseFloat(valStr.replace(/[+,]/g, ''))
                      const numFilter = parseFloat(f.value.replace(/[+,]/g, ''))
                      if (isNaN(val) || isNaN(numFilter)) return true
                      switch (f.operator) {
                        case 'gte': return val >= numFilter
                        case 'lte': return val <= numFilter
                        case 'gt': return val > numFilter
                        case 'lt': return val < numFilter
                        case 'eq': return val === numFilter
                        default: return true
                      }
                    })
                  })
                : pivotData
              return (
                <PivotTable
                  data={filteredPivotData}
                  allSelectedFields={selectedFields}
                  computedFields={activeComputed}
                  calculatedFields={calculatedFields}
                  fields={currentFields}
                  pageSize={10}
                />
              )
            })()
          ) : (
            <>
              <DataTable data={filteredData} selectedFields={selectedFields} computedMeasures={computedMeasures} calculatedFields={calculatedFields} fields={currentFields} allData={currentTableData} />
            </>
          )}
        </div>

        {/* 数据面板 */}
        {!isBrowseMode && (
          <DataPanel
            selectedFields={selectedFields}
            onToggleField={handleToggleField}
            onDragField={handleAddFilter}
            isPivotMode={isPivotMode}
            computedMeasures={computedMeasures}
            onAddComputedMeasure={handleAddComputedMeasure}
            onRemoveComputedMeasure={handleRemoveComputedMeasure}
            calculatedFields={calculatedFields}
            onAddCalculatedField={handleAddCalculatedField}
            onUpdateCalculatedField={f => { setEditingCalcField(f); setShowCalcBuilder(true) }}
            onRemoveCalculatedField={handleRemoveCalculatedField}
            fields={currentFields}
            fieldMetadata={fieldMetadata}
            onHoverField={handleHoverField}
          />
        )}
      </div>

      {/* AI 问数弹窗 - 已隐藏 */}
      {/* <AIChatModal
        open={showAIChatModal}
        onOpenChange={setShowAIChatModal}
        catalogId={asset?.catalogId || ''}
        catalogName={asset?.name || '对公大额存款变动情况'}
        onSaveToSpace={(result, question) => {
          console.log('保存到空间:', { result, question })
          setShowAIChatModal(false)
        }}
      /> */}

      {/* 保存报表弹窗 */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>保存报表到我的空间</DialogTitle>
            <DialogDescription>
              选择报表目录并填写报表名称，点击保存即可将报表保存到我的空间
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* 报表目录选择 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                <FolderOpen className="w-3.5 h-3.5 inline mr-1" />
                选择目录 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {REPORT_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => { setSaveCategoryId(cat.id); setSaveError('') }}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-all ${
                      saveCategoryId === cat.id
                        ? 'border-gcb-primary bg-blue-50 text-gcb-primary font-medium'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <FolderOpen className="w-4 h-4" />
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* 报表名称 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                报表名称 <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="请输入报表名称"
                value={saveName}
                onChange={(e) => { setSaveName(e.target.value); setSaveError('') }}
                className="h-9"
              />
            </div>

            {/* 报表描述 */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                报表描述
              </label>
              <Input
                placeholder="请输入报表描述（可选）"
                value={saveDescription}
                onChange={(e) => setSaveDescription(e.target.value)}
                className="h-9"
              />
            </div>

            {/* 错误提示 */}
            {saveError && (
              <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded">{saveError}</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="h-10 px-4">
              取消
            </Button>
            <Button onClick={handleConfirmSave} className="h-10 px-6 bg-gcb-primary hover:bg-gcb-primaryDark">
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 保存成功提示 */}
      {showSaveSuccess && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
          <div className="bg-black/60 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in zoom-in duration-200">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <span className="text-sm font-medium">报表"{savedReportName}"保存成功！</span>
          </div>
        </div>
      )}

      {/* 计算字段表达式构建器 */}
      <CalculatedFieldBuilder
        open={showCalcBuilder}
        onOpenChange={open => {
          setShowCalcBuilder(open)
          if (!open) setEditingCalcField(null)
        }}
        field={editingCalcField || { id: `calc-${Date.now()}`, name: '新计算字段', expression: [] }}
        fields={currentFields}
        onSave={handleSaveCalculatedField}
      />

      {/* 提交问题对话框 */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Headphones className="w-4 h-4 text-gcb-primary" />
              一键服务 — 提交问题
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="请描述您遇到的问题或需要帮助的内容..."
              value={serviceQuestion}
              onChange={(e) => setServiceQuestion(e.target.value)}
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-400">提交后服务中心将尽快回复，请耐心等待。</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowServiceDialog(false); setServiceQuestion('') }}>
              取消
            </Button>
            <Button
              onClick={handleServiceSubmit}
              disabled={!serviceQuestion.trim()}
              className="bg-gcb-primary hover:bg-gcb-primaryDark text-white"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              一键提交
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 字段元数据悬浮提示（fixed 定位，跟随字段位置，字段上方半透明显示） */}
      {hoveredField && fieldMetadata?.[hoveredField.id] && hoveredField.rect && (
        <div className="fixed z-[9999] pointer-events-none"
          style={{
            left: `${hoveredField.rect.left + 5}px`,
            top: `${hoveredField.rect.top - 4}px`,
            transform: 'translateY(-100%)',
          }}
        >
          <div className="bg-gray-900/80 text-white text-[11px] px-3 py-2 rounded shadow-xl max-w-[300px] space-y-1.5 backdrop-blur-sm">
            <div className="border-b border-white/20 pb-1.5">
              <span className="text-gray-300 text-[10px] font-medium">业务定义：</span>
              <span>{fieldMetadata[hoveredField.id].definition}</span>
            </div>
            <div>
              <span className="text-gray-300 text-[10px] font-medium">统计口径：</span>
              <span>{fieldMetadata[hoveredField.id].caliber}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
